// @ts-nocheck
import { useEffect, useRef } from 'react';
import {
  Chart,
  ScatterController,
  LineController,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-date-fns';

Chart.register(
  ScatterController,
  LineController,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const BENCHMARK_CSS = `
.benchmark-root {
  --bg: #0A1F22;
  --bg-2: #0D2528;
  --surface: #103a3e;
  --surface2: #144a4f;
  --border: #1e5f65;
  --border-strong: #266d73;
  --text: #FAF2E9;
  --text-muted: #97a8a0;
  --accent: #F0529C;
  --accent-2: #ec4899;
  --accent-glow: rgba(240, 82, 156, 0.28);
  --gold: #fbbf24;
  --silver: #cbd5e1;
  --bronze: #f97316;
  --green: #4ade80;
  --red: #f87171;

  font-family: 'Lato', -apple-system, BlinkMacSystemFont, sans-serif;
  background:
    radial-gradient(1200px 500px at 15% -10%, rgba(240,82,156,0.10), transparent 60%),
    radial-gradient(900px 400px at 85% -5%, rgba(99,102,241,0.08), transparent 60%),
    var(--bg);
  color: var(--text);
  min-height: 100vh;
  overflow-x: hidden;
}

.benchmark-root.light {
  --bg: #f7f5f0;
  --bg-2: #ffffff;
  --surface: #ffffff;
  --surface2: #f2ede4;
  --border: #e2ddcf;
  --border-strong: #cec7b3;
  --text: #1a2b2e;
  --text-muted: #5a6b6d;
  --accent: #d43c7a;
  --accent-2: #c53b70;
  --accent-glow: rgba(212, 60, 122, 0.18);
  color: var(--text);
  background: var(--bg);
}

.benchmark-root.light thead th {
  background: linear-gradient(180deg, #fce7f3, #fbcfe8);
  color: #831843;
  border-bottom-color: #f9a8d4;
}

.benchmark-root.light .sub-th {
  background: #fef2f6 !important;
  color: #9d174d !important;
  border-top-color: #f9a8d4;
}

.benchmark-root.light thead th[data-tooltip]::before,
.benchmark-root.light button[data-tooltip]::before {
  background: #ffffff;
  color: #1a2b2e;
  border: 1px solid #e2ddcf;
  box-shadow: 0 8px 24px rgba(30, 60, 65, 0.15);
}

.benchmark-root.light .table-container {
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(30, 60, 65, 0.08);
}

.benchmark-root.light tbody tr.data-row:hover {
  background: rgba(212, 60, 122, 0.06);
}

.benchmark-root.light .success-bar-bg {
  background: rgba(0,0,0,0.06);
}

.benchmark-root.light .expand-icon,
.benchmark-root.light .compare-check-box {
  background: rgba(0,0,0,0.03);
  border-color: var(--border);
}

.benchmark-root.light .filter-btn {
  background: #ffffff;
  color: var(--text);
}

.benchmark-root.light .task-dropdown {
  background: #ffffff;
  color: var(--text);
}

.benchmark-root.light .compare-modal {
  background: #ffffff;
  color: var(--text);
}

.benchmark-root.light .btn-callout-label {
  color: #fff;
}

.benchmark-root.light .rank-other {
  background: rgba(0,0,0,0.03);
  border-color: rgba(0,0,0,0.06);
}

.benchmark-root.light .detail-info-item {
  background: #ffffff;
}

.benchmark-root.light .object-chip {
  background: #ffffff;
}

.benchmark-root.light .theme-toggle {
  background: #ffffff;
  color: var(--text);
}

.benchmark-root .theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg, var(--surface2), color-mix(in oklab, var(--surface2) 92%, black));
  color: var(--text);
  font-size: 1.05rem;
  cursor: pointer;
  transition: all 0.18s ease;
  box-shadow: 0 1px 0 rgba(255,255,255,0.03) inset;
}

.benchmark-root .theme-toggle:hover {
  border-color: var(--accent);
  transform: translateY(-1px) rotate(15deg);
  box-shadow: 0 4px 12px rgba(240,82,156,0.14);
}

.benchmark-root * {
  box-sizing: border-box;
}

.benchmark-root .container {
  max-width: 100%;
  margin: 0 auto;
  padding: 4px 24px 0;
}

.benchmark-root .header {
  text-align: center;
  margin-bottom: 20px;
}

.benchmark-root .header h1 {
  font-family: 'Manrope', sans-serif;
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: var(--text);
  -webkit-text-fill-color: var(--text);
  background: none;
  -webkit-background-clip: unset;
  margin-bottom: 2px;
}

.benchmark-root .logo-title {
  height: 56px;
  margin-top: 26px;
  margin-bottom: 0;
  filter: drop-shadow(0 4px 20px rgba(240,82,156,0.15));
}

.benchmark-root .header p {
  color: var(--text-muted);
  font-size: 1.02rem;
  margin-top: 8px;
  letter-spacing: 0.02em;
}

.benchmark-root .task-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
}

.benchmark-root .task-selector label {
  font-family: 'Manrope', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.benchmark-root .task-dropdown {
  appearance: none;
  -webkit-appearance: none;
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text);
  padding: 10px 40px 10px 16px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%235a6b6d' viewBox='0 0 16 16'%3E%3Cpath d='M1.5 5.5l6.5 6.5 6.5-6.5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
}

.benchmark-root .task-dropdown:hover {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-glow);
}

.benchmark-root .task-dropdown:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-glow);
}

.benchmark-root .task-dropdown option {
  background: var(--surface);
  color: var(--text);
}

.benchmark-root .custom-task-picker {
  display: none;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 8px 18px;
  margin-bottom: 6px;
  flex-wrap: wrap;
  gap: 10px 14px;
  align-items: center;
}
.benchmark-root .custom-task-picker.visible {
  display: flex;
}
.benchmark-root .custom-task-picker-label {
  font-family: 'Manrope', sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-muted);
  width: 100%;
  margin-bottom: 2px;
}
.benchmark-root .custom-task-cb {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: var(--text);
  cursor: pointer;
  user-select: none;
}
.benchmark-root .custom-task-cb input[type="checkbox"] {
  accent-color: var(--accent);
  width: 15px;
  height: 15px;
  cursor: pointer;
}

.benchmark-root .ai2-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  opacity: 0.7;
}

.benchmark-root .ai2-bar img {
  height: 18px;
}

.benchmark-root .table-container {
  background: linear-gradient(180deg, var(--surface), color-mix(in oklab, var(--surface) 90%, black));
  border-radius: 14px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25), 0 1px 0 rgba(255,255,255,0.03) inset;
}

.benchmark-root .table-scroll {
  overflow-x: auto;
  scrollbar-width: none;
}

.benchmark-root .table-scroll::-webkit-scrollbar {
  display: none;
}

.benchmark-root table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1100px;
  margin-right: 0;
}

.benchmark-root thead {
  position: sticky;
  top: 0;
  z-index: 10;
}

.benchmark-root thead th {
  background: linear-gradient(180deg, var(--surface2), color-mix(in oklab, var(--surface2) 92%, black));
  padding: 9px 8px;
  text-align: left;
  font-family: 'Manrope', sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.9px;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border-strong);
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s, background 0.2s;
}

.benchmark-root .sub-th {
  font-size: 0.68rem;
  letter-spacing: 0.4px;
  padding: 6px 8px !important;
  background: var(--surface) !important;
  border-top: 1px solid var(--border);
  color: var(--text-muted);
}

.benchmark-root thead th:hover {
  color: var(--accent);
}

.benchmark-root thead th.sort-asc::after {
  content: ' ▲';
  font-size: 0.6rem;
}

.benchmark-root thead th.sort-desc::after {
  content: ' ▼';
  font-size: 0.6rem;
}

.benchmark-root thead th[data-tooltip],
.benchmark-root button[data-tooltip] {
  position: relative;
}

.benchmark-root thead th[data-tooltip]::before,
.benchmark-root button[data-tooltip]::before {
  content: attr(data-tooltip);
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #071f22;
  color: var(--text);
  font-family: 'Lato', sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  white-space: normal;
  width: 210px;
  padding: 8px 11px;
  border-radius: 7px;
  border: 1px solid var(--border);
  box-shadow: 0 6px 18px rgba(0,0,0,0.45);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s;
  z-index: 200;
  line-height: 1.5;
}

.benchmark-root thead th[data-tooltip]:hover::before,
.benchmark-root button[data-tooltip]:hover::before {
  opacity: 1;
}

.benchmark-root tbody tr {
  transition: background 0.15s;
}

.benchmark-root tbody tr:last-child {
  border-bottom: none;
}

.benchmark-root tbody tr:hover {
  background: transparent;
}

.benchmark-root tbody tr.data-row {
  cursor: pointer;
}

.benchmark-root tbody tr.data-row:hover {
  background: linear-gradient(90deg, rgba(240, 82, 156, 0.10), rgba(240, 82, 156, 0.02) 60%, transparent);
  box-shadow: inset 3px 0 0 var(--accent), 0 1px 0 rgba(240,82,156,0.10);
}

.benchmark-root tbody tr.compare-row-selected {
  background: rgba(240, 82, 156, 0.1) !important;
  box-shadow: inset 3px 0 0 var(--accent);
}

.benchmark-root .compare-check-box {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--border);
  font-size: 0.6rem;
  color: transparent;
  background: transparent;
  margin-right: 6px;
  flex-shrink: 0;
  transition: all 0.2s;
}

.benchmark-root .compare-row-selected .compare-check-box {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}

.benchmark-root td {
  padding: 4px 8px;
  font-size: 0.84rem;
  white-space: nowrap;
  vertical-align: middle;
  border-right: 1px solid var(--border);
  border-bottom: 1px solid rgba(30, 95, 101, 0.25);
}

.benchmark-root.light td {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.benchmark-root tbody tr:last-child td {
  border-bottom: none;
}

.benchmark-root td:last-child {
  border-right: none;
}

.benchmark-root thead th {
  border-right: 1px solid var(--border);
}

.benchmark-root thead th:last-child {
  border-right: none;
}

.benchmark-root .rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  transition: transform 0.15s ease;
}

.benchmark-root tbody tr.data-row:hover .rank {
  transform: scale(1.05);
}

.benchmark-root .rank-1 {
  background: linear-gradient(135deg, rgba(251, 191, 36, 0.28), rgba(251, 191, 36, 0.08));
  color: var(--gold);
  border: 1px solid rgba(251, 191, 36, 0.5);
  box-shadow: 0 0 12px rgba(251, 191, 36, 0.2);
  position: relative;
  overflow: hidden;
}
.benchmark-root .rank-1::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%);
  transform: translateX(-100%);
  animation: shimmer 3.5s ease-in-out infinite;
  pointer-events: none;
}
@keyframes shimmer {
  0% { transform: translateX(-100%); }
  50%, 100% { transform: translateX(100%); }
}

.benchmark-root .rank-2 {
  background: linear-gradient(135deg, rgba(203, 213, 225, 0.24), rgba(203, 213, 225, 0.08));
  color: var(--silver);
  border: 1px solid rgba(203, 213, 225, 0.4);
  box-shadow: 0 0 10px rgba(203, 213, 225, 0.12);
}

.benchmark-root .rank-3 {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.24), rgba(249, 115, 22, 0.08));
  color: var(--bronze);
  border: 1px solid rgba(249, 115, 22, 0.45);
  box-shadow: 0 0 10px rgba(249, 115, 22, 0.15);
}

.benchmark-root .rank-other {
  color: var(--text-muted);
  font-weight: 600;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.04);
}

.benchmark-root .policy-cell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.benchmark-root .policy-name {
  font-weight: 600;
  color: var(--text);
}

.benchmark-root .new-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 2px 7px;
  font-family: 'Manrope', sans-serif;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: #0A1F22;
  background: linear-gradient(90deg, #4ade80, #22c55e);
  border-radius: 999px;
  box-shadow: 0 0 8px rgba(74, 222, 128, 0.35);
  vertical-align: middle;
  animation: newPulse 2.4s ease-in-out infinite;
}

@keyframes newPulse {
  0%, 100% { box-shadow: 0 0 8px rgba(74, 222, 128, 0.35); }
  50%      { box-shadow: 0 0 14px rgba(74, 222, 128, 0.65); }
}

.benchmark-root .pill-indicators {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: 4px;
}

.benchmark-root .pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  font-family: 'Manrope', sans-serif;
  white-space: nowrap;
  cursor: default;
  border: 1px solid rgba(255,255,255,0.06);
  letter-spacing: 0.02em;
}

.benchmark-root .pill-type { background: rgba(240, 82, 156, 0.1); color: #d6336c; font-size: 0.85rem; }
.benchmark-root .pill-params { background: rgba(99, 102, 241, 0.1); color: #6366f1; font-size: 0.85rem; }
.benchmark-root .pill-chunk { background: rgba(22, 163, 74, 0.1); color: #16a34a; font-size: 0.85rem; }
.benchmark-root .pill-action-cartesian { background: rgba(8, 145, 178, 0.1); color: #0891b2; font-size: 0.78rem; }
.benchmark-root .pill-action-joint { background: rgba(234, 179, 8, 0.1); color: #ca8a04; font-size: 0.78rem; }
.benchmark-root .pill-action-ee { background: rgba(168, 85, 247, 0.1); color: #9333ea; font-size: 0.78rem; }
.benchmark-root .pill-action { background: rgba(90, 107, 109, 0.1); color: #5a6b6d; font-size: 0.78rem; }
.benchmark-root .pill-ctx { background: rgba(180, 130, 20, 0.1); color: #a87000; font-size: 0.85rem; }

.benchmark-root .affiliation-logo {
  height: 22px;
  width: auto;
  max-width: 36px;
  border-radius: 4px;
  object-fit: contain;
  vertical-align: middle;
}

.benchmark-root .affiliation-logo[alt="TUM"] {
  height: 11px;
  max-width: 18px;
}

.benchmark-root .success-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.benchmark-root .success-bar-bg {
  width: 88px;
  height: 8px;
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  box-shadow: inset 0 1px 1px rgba(0,0,0,0.25);
}

.benchmark-root .success-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;
}

.benchmark-root .success-bar::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0) 55%);
  pointer-events: none;
}

.benchmark-root .success-value {
  font-weight: 700;
  min-width: 38px;
  font-variant-numeric: tabular-nums;
}

.benchmark-root .type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.3px;
}

.benchmark-root .type-vla { background: rgba(240, 82, 156, 0.1); color: #d6336c; font-size: 0.85rem; }
.benchmark-root .type-wam { background: rgba(22, 163, 74, 0.1); color: #16a34a; font-size: 0.85rem; }
.benchmark-root .type-bm-vlm { background: rgba(180, 130, 20, 0.1); color: #a87000; font-size: 0.85rem; }
.benchmark-root .type-tamp { background: rgba(168, 85, 247, 0.12); color: #a855f7; font-size: 0.85rem; }
.benchmark-root .data-real { background: rgba(22, 163, 74, 0.1); color: #16a34a; font-size: 0.85rem; }
.benchmark-root .data-sim { background: rgba(8, 145, 178, 0.1); color: #0891b2; font-size: 0.85rem; }
.benchmark-root .data-mixed { background: rgba(180, 130, 20, 0.1); color: #a87000; font-size: 0.85rem; }
.benchmark-root .data-id { background: rgba(240, 82, 156, 0.1); color: #d6336c; font-size: 0.85rem; }
.benchmark-root .type-default { background: rgba(90, 107, 109, 0.1); color: #5a6b6d; font-size: 0.85rem; }

.benchmark-root .embodiment-droid { background: rgba(22, 163, 74, 0.1); color: #16a34a; font-size: 0.85rem; }
.benchmark-root .embodiment-cap { background: rgba(96, 165, 250, 0.12); color: #3b9edd; font-size: 0.85rem; }

.benchmark-root .badge-yes { color: var(--green); font-weight: 600; }
.benchmark-root .badge-no { color: var(--red); font-weight: 600; }

.benchmark-root .th-center { text-align: center; }
.benchmark-root .td-center { text-align: center; }

.benchmark-root .video-link {
  color: var(--accent);
  text-decoration: none;
  font-weight: 600;
  transition: opacity 0.2s;
}

.benchmark-root .video-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}

.benchmark-root .group-table-header {
  font-family: 'Manrope', sans-serif;
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-muted);
  padding: 8px 2px;
  margin-bottom: 0;
  background: transparent;
  border: none;
  display: block;
}

.benchmark-root .empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-muted);
}

.benchmark-root .empty-state p {
  font-size: 1.1rem;
  margin-bottom: 4px;
}

.benchmark-root .empty-state small {
  font-size: 0.85rem;
  opacity: 0.7;
}

.benchmark-root .group-header {
  background: rgba(240, 82, 156, 0.07);
  color: var(--accent);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--border);
}

.benchmark-root .sub-header {
  font-size: 0.72rem;
  color: var(--text-muted);
  font-weight: 600;
  background: rgba(240, 82, 156, 0.03);
}

.benchmark-root .ci-value {
  font-size: 0.75rem;
  font-weight: 500;
  opacity: 0.6;
}

.benchmark-root .paper-icon {
  font-size: 1.3rem;
  text-decoration: none;
  display: inline-block;
}

.benchmark-root .footer {
  text-align: center;
  margin-top: 32px;
  color: var(--text-muted);
  font-size: 0.8rem;
}

.benchmark-root .detail-row td {
  padding: 0;
  background: var(--surface2);
}

.benchmark-root .detail-content {
  padding: 16px 24px 20px;
  overflow: hidden;
}

.benchmark-root .detail-content h4 {
  font-family: 'Manrope', sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
}

.benchmark-root .object-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.benchmark-root .object-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 0.8rem;
}

.benchmark-root .object-chip .obj-name {
  color: var(--text);
  font-weight: 500;
}

.benchmark-root .object-chip .obj-rate {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.benchmark-root .detail-info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.benchmark-root .detail-info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 14px;
  min-width: 140px;
}

.benchmark-root .detail-label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-muted);
  font-family: 'Manrope', sans-serif;
}

.benchmark-root .detail-value {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text);
}

.benchmark-root .date-cell {
  white-space: nowrap;
  font-size: 0.82rem;
}

.benchmark-root #leaderboard.hide-gen-col th[data-col="gen_tasks"],
.benchmark-root #leaderboard.hide-gen-col td.gen-tasks-td {
  display: none;
}

/* Column show/hide toggles */
.benchmark-root #leaderboard.hide-col-data_source th[data-col="data_source"],
.benchmark-root #leaderboard.hide-col-data_source td[data-col="data_source"],
.benchmark-root #leaderboard-2.hide-col-data_source th[data-col="data_source"],
.benchmark-root #leaderboard-2.hide-col-data_source td[data-col="data_source"] { display: none; }
.benchmark-root #leaderboard.hide-col-data_type th[data-col="data_type"],
.benchmark-root #leaderboard.hide-col-data_type td[data-col="data_type"] { display: none; }
.benchmark-root #leaderboard.hide-col-gen_tasks th[data-col="gen_tasks"],
.benchmark-root #leaderboard.hide-col-gen_tasks td[data-col="gen_tasks"] { display: none; }
.benchmark-root #leaderboard.hide-col-robot_generalist th[data-col="robot_generalist"],
.benchmark-root #leaderboard.hide-col-robot_generalist td[data-col="robot_generalist"] { display: none; }
.benchmark-root #leaderboard.hide-col-requires_planning th[data-col="requires_planning"],
.benchmark-root #leaderboard.hide-col-requires_planning td[data-col="requires_planning"],
.benchmark-root #leaderboard-2.hide-col-requires_planning th[data-col="requires_planning"],
.benchmark-root #leaderboard-2.hide-col-requires_planning td[data-col="requires_planning"] { display: none; }
.benchmark-root #leaderboard.hide-col-data_open_sourced th[data-col="data_open_sourced"],
.benchmark-root #leaderboard.hide-col-data_open_sourced td[data-col="data_open_sourced"] { display: none; }
.benchmark-root #leaderboard.hide-col-model_open_sourced th[data-col="model_open_sourced"],
.benchmark-root #leaderboard.hide-col-model_open_sourced td[data-col="model_open_sourced"] { display: none; }
.benchmark-root #leaderboard.hide-col-paper th[data-col="paper"],
.benchmark-root #leaderboard.hide-col-paper td[data-col="paper"] { display: none; }
.benchmark-root #leaderboard.hide-col-type th[data-col="type"],
.benchmark-root #leaderboard.hide-col-type td[data-col="type"] { display: none; }
.benchmark-root #leaderboard.hide-col-embodiment th[data-col="embodiment"],
.benchmark-root #leaderboard.hide-col-embodiment td[data-col="embodiment"] { display: none; }
.benchmark-root #leaderboard.hide-col-action_space th[data-col="action_space"],
.benchmark-root #leaderboard.hide-col-action_space td[data-col="action_space"] { display: none; }
.benchmark-root #leaderboard.hide-col-parameters th[data-col="parameters"],
.benchmark-root #leaderboard.hide-col-parameters td[data-col="parameters"] { display: none; }
.benchmark-root #leaderboard.hide-col-chunk_size th[data-col="chunk_size"],
.benchmark-root #leaderboard.hide-col-chunk_size td[data-col="chunk_size"] { display: none; }
.benchmark-root #leaderboard.hide-col-context_length th[data-col="context_length"],
.benchmark-root #leaderboard.hide-col-context_length td[data-col="context_length"] { display: none; }
.benchmark-root #leaderboard.hide-col-smoothness th[data-col="smoothness"],
.benchmark-root #leaderboard.hide-col-smoothness td[data-col="smoothness"] { display: none; }
.benchmark-root #leaderboard th[data-col="oracle_rate"],
.benchmark-root #leaderboard td[data-col="oracle_rate"],
.benchmark-root #leaderboard-2 th[data-col="oracle_rate"],
.benchmark-root #leaderboard-2 td[data-col="oracle_rate"] { display: none; }
.benchmark-root #leaderboard.show-oracle-rate th[data-col="oracle_rate"],
.benchmark-root #leaderboard.show-oracle-rate td[data-col="oracle_rate"],
.benchmark-root #leaderboard-2.show-oracle-rate th[data-col="oracle_rate"],
.benchmark-root #leaderboard-2.show-oracle-rate td[data-col="oracle_rate"] { display: table-cell; }
.benchmark-root th[data-col="task_count"],
.benchmark-root td[data-col="task_count"] { display: none; }
.benchmark-root #leaderboard.show-task-count th[data-col="task_count"],
.benchmark-root #leaderboard.show-task-count td[data-col="task_count"],
.benchmark-root #leaderboard-2.show-task-count th[data-col="task_count"],
.benchmark-root #leaderboard-2.show-task-count td[data-col="task_count"] { display: table-cell; }
.benchmark-root #leaderboard.hide-success-rate th[data-col="success_rate"],
.benchmark-root #leaderboard.hide-success-rate td[data-col="success_rate"],
.benchmark-root #leaderboard-2.hide-success-rate th[data-col="success_rate"],
.benchmark-root #leaderboard-2.hide-success-rate td[data-col="success_rate"] { display: none; }
.benchmark-root #leaderboard.hide-col-date_released th[data-col="date_released"],
.benchmark-root #leaderboard.hide-col-date_released td[data-col="date_released"] { display: none; }

/* Column picker dropdown */
.benchmark-root .col-picker-wrap { position: relative; }
.benchmark-root .col-picker-panel {
  display: none;
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
  z-index: 200;
  min-width: 190px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
}
.benchmark-root .col-picker-panel.open { display: block; }
.benchmark-root .col-picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  font-size: 0.81rem;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
  color: var(--text);
}
.benchmark-root .col-picker-item:hover { background: rgba(255,255,255,0.06); }
.benchmark-root .col-picker-item input[type="checkbox"] { cursor: pointer; accent-color: var(--accent); }

/* Column-level filter button inside th */
.benchmark-root thead th { position: relative; }
.benchmark-root .col-filter-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  padding: 0 3px;
  font-size: 0.7rem;
  line-height: 1;
  background: none;
  border: 1px solid transparent;
  border-radius: 3px;
  color: var(--text-muted);
  cursor: pointer;
  vertical-align: middle;
  transition: color 0.15s, border-color 0.15s;
}
.benchmark-root .col-filter-btn:hover { color: var(--text); border-color: var(--border); }
.benchmark-root .col-filter-btn.active { color: var(--accent); border-color: var(--accent); }
.benchmark-root .col-filter-panel {
  display: none;
  position: absolute;
  top: calc(100% + 2px);
  left: 50%;
  transform: translateX(-50%);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 6px;
  z-index: 300;
  min-width: 160px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  white-space: nowrap;
}
.benchmark-root .col-filter-panel.open { display: block; }
.benchmark-root .col-filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  font-size: 0.81rem;
  cursor: pointer;
  border-radius: 4px;
  user-select: none;
  color: var(--text);
  font-weight: 400;
}
.benchmark-root .col-filter-item:hover { background: rgba(255,255,255,0.06); }
.benchmark-root .col-filter-item input[type="checkbox"] { cursor: pointer; accent-color: var(--accent); }

@keyframes rowIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes lb-spin {
  to { transform: rotate(360deg); }
}

.benchmark-root tbody tr.data-row {
  animation: rowIn 0.25s ease both;
}

.benchmark-root tbody tr.data-row:hover .expand-icon {
  background: rgba(240, 82, 156, 0.15);
  color: var(--accent);
}

.benchmark-root .expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, background 0.2s, color 0.2s;
  font-size: 0.65rem;
  color: var(--text-muted);
  background: rgba(255,255,255,0.05);
  border-radius: 4px;
  width: 20px;
  height: 20px;
  margin-right: 6px;
  flex-shrink: 0;
}

.benchmark-root .expand-icon.open {
  transform: rotate(90deg);
  background: rgba(240, 82, 156, 0.15);
  color: var(--accent);
}

.benchmark-root .submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--accent);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(240, 82, 156, 0.3);
}

.benchmark-root .submit-btn:hover {
  background: #d6357f;
  box-shadow: 0 4px 14px rgba(240, 82, 156, 0.4);
}

.benchmark-root .btn-callout-wrap {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}
.benchmark-root .btn-callout-label {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  background: var(--accent);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(240, 82, 156, 0.35);
  pointer-events: none;
}
.benchmark-root .btn-callout-label::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: var(--accent);
}
@keyframes calloutBob {
  0%, 100% { transform: translateX(-50%) translateY(0); }
  50%       { transform: translateX(-50%) translateY(-4px); }
}
.benchmark-root .btn-callout-label-inline {
  position: relative;
  display: inline-block;
  white-space: nowrap;
  background: var(--accent);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 5px 16px 5px 10px;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(240, 82, 156, 0.35);
  pointer-events: none;
}
.benchmark-root .btn-callout-label-inline::after {
  content: '';
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  border: 5px solid transparent;
  border-left-color: var(--accent);
}
@keyframes calloutBobInline {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-4px); }
}

.benchmark-root .faq-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #16a34a;
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  text-decoration: none;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(22, 163, 74, 0.3);
}
.benchmark-root .faq-link:hover {
  background: #15803d;
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.4);
  color: #fff;
}

.benchmark-root .faq-section {
  max-width: 820px;
  margin: 32px auto 0;
  padding: 0 24px;
  scroll-margin-top: 80px;
}
.benchmark-root .faq-section-title {
  font-family: 'Manrope', sans-serif;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--text);
  margin: 0 0 24px;
}
.benchmark-root .faq-item {
  border-top: 1px solid var(--border);
  padding: 20px 0;
}
.benchmark-root .faq-item:last-child {
  border-bottom: 1px solid var(--border);
}
.benchmark-root .faq-question {
  font-family: 'Manrope', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text);
  margin: 0 0 10px;
}
.benchmark-root .faq-answer {
  display: block;
  font-family: 'Lato', sans-serif;
  font-size: 0.88rem;
  color: var(--text-muted);
  line-height: 1.75;
  margin: 0;
}

.benchmark-root .filter-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: var(--surface2);
  color: var(--text-muted);
  font-family: 'Manrope', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 10px;
  border: 1.5px solid var(--border);
  cursor: pointer;
  transition: all 0.2s ease;
}

.benchmark-root .filter-btn:hover {
  border-color: var(--accent);
  color: var(--text);
}

.benchmark-root .filter-btn.active {
  background: rgba(240, 82, 156, 0.12);
  border-color: var(--accent);
  color: var(--text);
}

.benchmark-root .filter-radio {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--text-muted);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: border-color 0.2s;
}

.benchmark-root .filter-radio::after {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  opacity: 0;
  transition: opacity 0.2s;
}

.benchmark-root .filter-btn.active .filter-radio {
  border-color: var(--accent);
}

.benchmark-root .filter-btn.active .filter-radio::after {
  opacity: 1;
}

.benchmark-root .compare-modal-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(5,25,28,0.82);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.benchmark-root .compare-modal-overlay.visible { display: flex; }
.benchmark-root .compare-modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  max-width: 860px;
  width: 100%;
  max-height: 92vh;
  overflow-y: auto;
  padding: 20px 20px 24px;
  position: relative;
}
.benchmark-root .compare-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1.4rem;
  cursor: pointer;
  line-height: 1;
  transition: color 0.2s;
}
.benchmark-root .compare-modal-close:hover { color: var(--text); }
.benchmark-root .compare-modal h3 {
  font-family: 'Manrope', sans-serif;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 20px;
  color: var(--text);
}
.benchmark-root .compare-cols { display: flex; flex-direction: column; gap: 2px; }
.benchmark-root .compare-legend { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 0.82rem; font-family: 'Manrope', sans-serif; font-weight: 700; }
.benchmark-root .compare-delta-header {
  display: grid;
  grid-template-columns: 1fr 130px 1fr;
  align-items: center;
  gap: 0;
  margin-bottom: 10px;
  padding: 0 4px 10px;
  border-bottom: 1px solid var(--border);
}
.benchmark-root .compare-delta-header .hdr-policy { font-family: 'Manrope', sans-serif; font-weight: 800; font-size: 0.82rem; padding: 5px 10px; border-radius: 20px; display: inline-block; }
.benchmark-root .compare-delta-header .hdr-center { text-align: center; font-family: 'Manrope', sans-serif; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: var(--text-muted); }
.benchmark-root .compare-delta-row { display: grid; grid-template-columns: 1fr 130px 1fr; align-items: center; gap: 0; font-size: 0.82rem; border-radius: 6px; padding: 2px 0; }
.benchmark-root .compare-delta-row:nth-child(even) { background: rgba(255,255,255,0.03); }
.benchmark-root .compare-delta-name { font-family: 'Manrope', sans-serif; font-weight: 600; color: var(--text); font-size: 0.8rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 6px 10px; text-align: center; }
.benchmark-root .compare-delta-bar-left { display: flex; justify-content: flex-end; align-items: center; gap: 7px; height: 30px; overflow: hidden; min-width: 0; padding: 0 8px; }
.benchmark-root .compare-delta-bar-right { display: flex; justify-content: flex-start; align-items: center; gap: 7px; height: 30px; overflow: hidden; min-width: 0; padding: 0 8px; }
.benchmark-root .compare-delta-fill { height: 14px; border-radius: 7px; flex-shrink: 0; transition: width 0.45s cubic-bezier(0.4,0,0.2,1); }
.benchmark-root .compare-delta-pct { font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 0.72rem; flex-shrink: 0; white-space: nowrap; padding: 2px 7px; border-radius: 10px; }
.benchmark-root .compare-delta-label { text-align: center; font-family: 'Manrope', sans-serif; font-weight: 700; font-size: 0.75rem; white-space: nowrap; }
.benchmark-root .compare-col-title { font-family: 'Manrope', sans-serif; font-size: 0.9rem; font-weight: 700; color: var(--accent); margin-bottom: 14px; padding-bottom: 8px; border-bottom: 1px solid var(--border); }
.benchmark-root .compare-obj-row { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; font-size: 0.82rem; }
.benchmark-root .compare-obj-name { width: 110px; flex-shrink: 0; color: var(--text-muted); font-family: 'Lato', sans-serif; }
.benchmark-root .compare-bar-bg { flex: 1; background: var(--surface2); border-radius: 4px; height: 8px; overflow: hidden; }
.benchmark-root .compare-bar { height: 100%; border-radius: 4px; transition: width 0.4s ease; }
.benchmark-root .compare-rate { width: 40px; text-align: right; font-weight: 600; font-family: 'Manrope', sans-serif; font-size: 0.78rem; }
.benchmark-root .compare-diff-badge { display: inline-block; font-size: 0.7rem; font-weight: 700; padding: 1px 5px; border-radius: 4px; min-width: 36px; text-align: center; }
.benchmark-root .diff-pos { background: rgba(74,222,128,0.15); color: #4ade80; }
.benchmark-root .diff-neg { background: rgba(248,113,113,0.15); color: #f87171; }
.benchmark-root .diff-zero { background: rgba(168,184,176,0.12); color: var(--text-muted); }
.benchmark-root .compare-row-selected { background: rgba(240,82,156,0.1) !important; outline: 1.5px solid var(--accent); outline-offset: -1px; }
.benchmark-root tr.compare-row-selected .compare-check-box { background: var(--accent); border-color: var(--accent); color: #fff; }

.benchmark-root .compare-modal.progress-modal { max-width: 92vw; width: 92vw; max-height: 90vh; display: flex; flex-direction: column; }
.benchmark-root .progress-chart-row { display: flex; gap: 24px; align-items: flex-start; flex: 1; min-height: 0; }
.benchmark-root .progress-chart-wrapper { flex: 1; min-width: 0; height: 600px; }
.benchmark-root .progress-legend-list { flex-shrink: 0; width: 200px; display: flex; flex-direction: column; gap: 7px; padding-top: 4px; overflow-y: auto; max-height: 600px; }

.benchmark-root .chart-wrapper {
  position: relative;
  height: 360px;
  width: 100%;
}

.benchmark-root .chart-container {
  background: #FAF2E9;
  border-radius: 16px;
  border: 1px solid var(--border);
  padding: 20px 24px;
  margin-top: 24px;
  margin-bottom: 0;
  width: 360px;
  max-width: 100%;
  box-shadow: none;
}

.benchmark-root .chart-container h2 {
  font-family: 'Manrope', sans-serif;
  font-size: 1rem;
  font-weight: 700;
  color: #0A3235;
  margin-bottom: 16px;
  text-transform: none;
  letter-spacing: 0;
}

.benchmark-root .chart-toggle-wrap {
  text-align: center;
  margin-bottom: 16px;
}

.benchmark-root .chart-toggle-btn {
  background: var(--surface);
  border: 1px solid var(--border);
  color: var(--text-muted);
  font-family: 'Manrope', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.benchmark-root .chart-toggle-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.benchmark-root .chart-collapsible {
  overflow: hidden;
  transition: max-height 0.4s ease, opacity 0.3s ease;
  max-height: 500px;
  opacity: 1;
}

.benchmark-root .chart-collapsible.collapsed {
  max-height: 0;
  opacity: 0;
}

@media (max-width: 900px) {
  .benchmark-root .task-selector { flex-wrap: wrap; gap: 8px; }
}

@media (max-width: 640px) {
  .benchmark-root { font-size: 15px; }
  .benchmark-root .header h1 { font-size: 1.4rem; }
  .benchmark-root .logo-title { height: 40px; }
  .benchmark-root .container { padding: 16px 12px; }
  .benchmark-root .task-selector { flex-direction: column; align-items: stretch; gap: 8px; margin-bottom: 20px; }
  .benchmark-root .task-selector label { display: none; }
  .benchmark-root .task-dropdown { width: 100%; font-size: 0.9rem; padding: 10px 14px; }
  .benchmark-root .submit-btn, .benchmark-root .filter-btn { width: 100%; justify-content: center; padding: 10px 16px; font-size: 0.85rem; touch-action: manipulation; }
  .benchmark-root .table-scroll { scrollbar-width: thin; scrollbar-color: var(--border) transparent; }
  .benchmark-root .table-scroll::-webkit-scrollbar { display: block; height: 4px; }
  .benchmark-root .table-scroll::-webkit-scrollbar-track { background: transparent; }
  .benchmark-root .table-scroll::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
  .benchmark-root thead th, .benchmark-root td { padding: 8px 10px; font-size: 0.78rem; }
  .benchmark-root .success-bar-bg { width: 52px; }
  .benchmark-root .success-value { min-width: 30px; font-size: 0.82rem; }
  .benchmark-root .compare-modal-overlay { padding: 0; align-items: flex-end; }
  .benchmark-root .compare-modal { border-radius: 16px 16px 0 0; max-height: 88vh; width: 100%; padding: 16px 14px 24px; }
  .benchmark-root .progress-chart-outer { width: 100% !important; }
  .benchmark-root .progress-chart-row { flex-direction: column !important; }
  .benchmark-root .progress-chart-wrapper { height: 300px !important; }
  .benchmark-root .progress-legend-list { width: 100% !important; flex-direction: row !important; flex-wrap: wrap !important; gap: 6px !important; padding-top: 10px !important; max-height: none !important; }
  .benchmark-root .controls-row { flex-direction: column !important; align-items: stretch !important; }
  .benchmark-root .controls-row > div { width: 100%; }
  .benchmark-root .controls-row .filter-btn { width: 100%; }
  .benchmark-root #compare-hint { font-size: 0.72rem !important; }
  .benchmark-root .detail-info-grid { grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); }
  .benchmark-root .date-cell { white-space: normal; min-width: 56px; max-width: 72px; word-break: break-word; font-size: 0.75rem; line-height: 1.3; }
  .benchmark-root .object-chip { font-size: 0.74rem; padding: 5px 10px; }
}
`;

export const BenchmarkAndLeaderboard = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const styleRef = useRef<HTMLStyleElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const chartRef2 = useRef<Chart | null>(null);

  useEffect(() => {
    // Inject scoped styles
    const style = document.createElement('style');
    style.textContent = BENCHMARK_CSS;
    document.head.appendChild(style);
    styleRef.current = style;

    // Load Google Fonts
    const fonts = [
      'https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap',
      'https://fonts.googleapis.com/css2?family=Manrope:wght@200;300;400;500;600;700;800&display=swap',
    ];
    const fontLinks: HTMLLinkElement[] = [];
    fonts.forEach((href) => {
      if (!document.querySelector(`link[href="${href}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        document.head.appendChild(link);
        fontLinks.push(link);
      }
    });

    return () => {
      if (styleRef.current) {
        document.head.removeChild(styleRef.current);
      }
      fontLinks.forEach((l) => {
        if (l.parentNode) l.parentNode.removeChild(l);
      });
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      if (chartRef2.current) {
        chartRef2.current.destroy();
        chartRef2.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    // ---- State ----
    let currentData: Record<string, string>[] = [];
    let taskObjectRates: Record<string, { name: string; rate: number }[]> = {};
    let tasksList:
      | {
          id: string;
          label: string;
          label_short?: string;
          policies: string[];
          combined?: boolean;
          showObjects?: boolean;
          allowCompare?: boolean;
        }[]
      | null = null;
    let filterGeneralists = false;
    let filterNonID = true;
    let hiddenCols: Set<string> = new Set();
    let colFilters: Record<string, Set<string>> = {};
    let compareMode = false;
    let compareSelected: string[] = [];
    let renderedRows: Record<string, string>[] = [];
    const ORACLE_RATE_TASKS = new Set(['mb_pick_classic', 'mb_pick_filament', 'mb_pick_msproc', 'mb_pick_rand_cam', 'mb_pnp', 'mb_pnp_color', 'mb_pnp_next_to', 'combined_easy', 'combined_main', 'combined_custom']);
    const ORACLE_ONLY_TASKS = new Set(['mb_pick_classic', 'mb_pick_filament', 'mb_pick_msproc', 'mb_pick_rand_cam']);
    const TASK_COUNT_TASKS = new Set(['combined_easy', 'combined_main', 'combined_custom', 'combined_commonsense']);
    // MolmoSpaces-Commonsense tasks (cs_*) form their own family, kept out of the other combined views
    const isCommonsenseTask = (id: string) => id.startsWith('cs_');
    const eligibleForView = (viewId: string) =>
      (tasksList || []).filter((t) => !t.combined && t.id !== 'ms_nav_multitype' && isCommonsenseTask(t.id) === (viewId === 'combined_commonsense'));
    let customSelectedTasks: string[] = [];
    const ORACLE_LABEL_TASKS = new Set(['ms_pick', 'ms_pick_place', 'ms_open', 'ms_close']);
    let currentTaskFlags = { showObjects: true, allowCompare: true, showOracleRate: false, oracleLabel: false };
    let currentTaskId = '';
    let sortCol = 'oracle_rate';
    let sortDir = 'desc';

    const logos: Record<string, string> = {
      NYU: '/benchmark/media/logos/nyu.png',
      Berkeley: '/benchmark/media/logos/berkeley.png',
      'NVIDIA GEAR': '/benchmark/media/logos/nvidia.png',
      'NVIDIA': '/benchmark/media/logos/nvidia.png',
      'PI': '/benchmark/media/logos/pi.png',
      'Ai2': '/benchmark/media/logos/ai2_logo.jpeg',
      'Shanghai AI Laboratory': '/benchmark/media/logos/intern_robotics.png',
      'Galbot / Peking University': '/benchmark/media/logos/galbot.png',
      'AIR': '/benchmark/media/logos/air_logo.png',
      'Princeton': '/benchmark/media/logos/princeton.png',
      'PsiBot': '/benchmark/media/logos/psi.png',
      'MIT': '/benchmark/media/logos/mit.png',
      'UPenn': '/benchmark/media/logos/upenn.png',
      'TeleAI': '/benchmark/media/logos/teleai.png',
      'X Square Robot': '/benchmark/media/logos/xr.png',
      'Lumos': '/benchmark/media/logos/lumos.jpg',
      'USTB': '/benchmark/media/logos/ustb.png',
      'Geodesic': '/benchmark/media/logos/geodesic.jpg',
      'Zoellent AI': '/benchmark/media/logos/zoellent.png',
      'TUM': '/benchmark/media/logos/tum.png',
      'MagicLab': '/benchmark/media/logos/magiclab.jpg',
      'awomo': '/benchmark/media/logos/awomo.png',
    };

    // ---- Column visibility ----
    const TOGGLEABLE_COLS = [
      { key: 'data_source', label: 'MolmoSpaces Data' },
      { key: 'data_type', label: 'Data Type' },
      { key: 'gen_tasks', label: 'Multi Task' },
      { key: 'robot_generalist', label: 'Robot Gen.' },
      { key: 'requires_planning', label: 'Planning Needed' },
      { key: 'data_open_sourced', label: 'Open Sourced: Data' },
      { key: 'model_open_sourced', label: 'Open Sourced: Model' },
      { key: 'paper', label: 'Source' },
      { key: 'type', label: 'Model Type' },
      { key: 'embodiment', label: 'Embodiment' },
      { key: 'action_space', label: 'Action Space' },
      { key: 'parameters', label: 'Parameters' },
      { key: 'smoothness', label: 'Joint Jerk' },
      { key: 'date_released', label: 'Date Released' },
    ];

    function applyHiddenCols() {
      const table = root.querySelector('#leaderboard') as HTMLElement;
      if (!table) return;
      TOGGLEABLE_COLS.forEach(({ key }) => {
        const hidden = hiddenCols.has(key) || (key === 'data_source' && filterNonID);
        table.classList.toggle(`hide-col-${key}`, hidden);
      });
      const table2 = root.querySelector('#leaderboard-2') as HTMLElement;
      if (table2) {
        TOGGLEABLE_COLS.forEach(({ key }) => {
          const hidden = hiddenCols.has(key) || (key === 'data_source' && filterNonID);
          table2.classList.toggle(`hide-col-${key}`, hidden);
        });
      }
      updateOpenSourcedHeader();
    }

    function updateOpenSourcedHeader() {
      const table = root.querySelector('#leaderboard') as HTMLElement;
      if (!table) return;
      const groupTh = table.querySelector('th[data-col="open_sourced_group"]') as HTMLTableCellElement;
      if (!groupTh) return;
      const dataHidden = hiddenCols.has('data_open_sourced');
      const modelHidden = hiddenCols.has('model_open_sourced');
      if (dataHidden && modelHidden) {
        groupTh.style.display = 'none';
      } else {
        groupTh.style.display = '';
        groupTh.colSpan = dataHidden || modelHidden ? 1 : 2;
      }
    }

    function countVisibleCols() {
      // 4 always-visible cols: rank, policy, affiliation, success_rate
      const oracle = currentTaskFlags.showOracleRate ? 1 : 0;
      const hiddenSr = currentTaskFlags.oracleOnly ? 1 : 0;
      const taskCount = currentTaskFlags.showTaskCount ? 1 : 0;
      return 4 - hiddenSr + oracle + taskCount + TOGGLEABLE_COLS.filter(({ key }) => !hiddenCols.has(key)).length;
    }

    function renderColPickerPanel() {
      const panel = root.querySelector('#col-picker-panel') as HTMLElement;
      if (!panel) return;
      panel.innerHTML = TOGGLEABLE_COLS.map(
        ({ key, label }) =>
          `<label class="col-picker-item">
            <input type="checkbox" data-col-key="${key}" ${hiddenCols.has(key) ? '' : 'checked'}>
            ${label}
          </label>`
      ).join('');
      panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener('change', () => {
          if (cb.checked) hiddenCols.delete(cb.dataset.colKey!);
          else hiddenCols.add(cb.dataset.colKey!);
          applyHiddenCols();
        });
      });
    }

    function toggleColPicker() {
      const panel = root.querySelector('#col-picker-panel') as HTMLElement;
      if (!panel) return;
      const isOpen = panel.classList.toggle('open');
      if (isOpen) renderColPickerPanel();
    }

    // ---- Column filters ----
    const COL_FILTER_DEFS: Record<string, { extract: (row: Record<string, string>) => string; options: { value: string; label: string }[] }> = {
      data_source: {
        extract: (row) => (row['Data Source'] || '').toLowerCase() === 'id' ? 'id' : 'non_id',
        options: [
          { value: 'id', label: '<span class="badge-yes">✓</span> In-Distribution' },
          { value: 'non_id', label: '<span class="badge-no">\u2717</span> Non-ID' },
        ],
      },
      data_type: {
        extract: (row) => (row['Data Type'] || '').toLowerCase() || '\u2014',
        options: [
          { value: 'real', label: '<span class="pill data-real">Real</span>' },
          { value: 'mixed', label: '<span class="pill data-mixed">Mixed</span>' },
          { value: 'sim', label: '<span class="pill data-sim">Sim</span>' },
        ],
      },
      gen_tasks: {
        extract: (row) => (row['Gen Tasks'] || '').toLowerCase() === 'yes' ? 'yes' : 'no',
        options: [
          { value: 'yes', label: '<span class="badge-yes">✓</span> Yes' },
          { value: 'no', label: '<span class="badge-no">\u2717</span> No' },
        ],
      },
      robot_generalist: {
        extract: (row) => {
          const rg = (row['Robot Generalist'] || '').trim();
          if (rg === '~') return 'partial';
          return rg.toLowerCase() === 'yes' ? 'yes' : 'no';
        },
        options: [
          { value: 'yes', label: '<span class="badge-yes">✓</span> Yes' },
          { value: 'no', label: '<span class="badge-no">\u2717</span> No' },
          { value: 'partial', label: '<span style="color:var(--text-muted)">~</span> Partial' },
        ],
      },
      data_open_sourced: {
        extract: (row) => (row.Policy || '').includes('\u03c0') ? 'no' : 'yes',
        options: [
          { value: 'yes', label: '<span class="badge-yes">✓</span> Yes' },
          { value: 'no', label: '<span class="badge-no">\u2717</span> No' },
        ],
      },
      model_open_sourced: {
        extract: (row) => (row['Open Sourced'] || '').toLowerCase() === 'yes' ? 'yes' : 'no',
        options: [
          { value: 'yes', label: '<span class="badge-yes">✓</span> Yes' },
          { value: 'no', label: '<span class="badge-no">\u2717</span> No' },
        ],
      },
      type: {
        extract: (row) => (row['Type'] || '').toLowerCase(),
        options: [
          { value: 'vla', label: '<span class="pill type-vla">VLA</span>' },
          { value: 'vlm + bm', label: '<span class="pill type-bm-vlm">VLM + BM</span>' },
          { value: 'wam', label: '<span class="pill type-wam">WAM</span>' },
        ],
      },
      embodiment: {
        extract: (row) => (row['Embodiment'] || '').toLowerCase(),
        options: [
          { value: 'droid', label: '<span class="pill embodiment-droid">DROID</span>' },
          { value: 'franka + cap', label: '<span class="pill embodiment-cap">Franka + CAP</span>' },
        ],
      },
      action_space: {
        extract: (row) => (row['Action Space'] || '').toLowerCase(),
        options: [
          { value: '7-dof joint pos.', label: '<span class="pill pill-action-joint">Joint Pos.</span>' },
          { value: '6-dof eef cart.', label: '<span class="pill pill-action-cartesian">EEF Cart.</span>' },
        ],
      },
    };

    function updateColFilterBtn(colKey: string) {
      const btn = root.querySelector(`#col-filter-btn-${colKey}`) as HTMLElement;
      if (!btn) return;
      const active = (colFilters[colKey]?.size ?? 0) > 0;
      btn.classList.toggle('active', active);
    }

    function renderColFilterPanel(colKey: string) {
      const panel = root.querySelector(`#col-filter-panel-${colKey}`) as HTMLElement;
      if (!panel) return;
      const def = COL_FILTER_DEFS[colKey];
      if (!def) return;
      const selected = colFilters[colKey] ?? new Set<string>();
      panel.innerHTML = def.options.map(
        ({ value, label }) =>
          `<label class="col-filter-item">
            <input type="checkbox" data-col-key="${colKey}" data-value="${value}" ${selected.has(value) ? 'checked' : ''}>
            ${label}
          </label>`
      ).join('');
      panel.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener('change', () => {
          if (!colFilters[colKey]) colFilters[colKey] = new Set();
          if (cb.checked) colFilters[colKey].add(cb.dataset.value!);
          else colFilters[colKey].delete(cb.dataset.value!);
          updateColFilterBtn(colKey);
          renderTable();
          renderChart();
        });
      });
    }

    function toggleColFilterPanel(colKey: string) {
      // Close all other col filter panels first
      root.querySelectorAll('.col-filter-panel.open').forEach((el) => {
        if ((el as HTMLElement).id !== `col-filter-panel-${colKey}`) el.classList.remove('open');
      });
      const panel = root.querySelector(`#col-filter-panel-${colKey}`) as HTMLElement;
      if (!panel) return;
      const isOpen = panel.classList.toggle('open');
      if (isOpen) renderColFilterPanel(colKey);
    }

    // ---- Helpers ----
    function toggleCompareMode() {
      compareMode = !compareMode;
      compareSelected = [];
      const btn = root.querySelector('#compare-btn') as HTMLElement;
      if (btn) btn.classList.toggle('active', compareMode);
      renderTable();
    }

    function selectForCompare(idx: number) {
      const policyName = (renderedRows[idx] || {}).Policy || '';
      if (!policyName) return;
      const selIdx = compareSelected.indexOf(policyName);
      if (selIdx >= 0) {
        compareSelected.splice(selIdx, 1);
      } else if (compareSelected.length < 2) {
        compareSelected.push(policyName);
      } else {
        compareSelected = [compareSelected[1], policyName];
      }
      renderTable();
      if (compareSelected.length === 2) openCompareModal();
    }

    function openCompareModal() {
      const [a, b] = compareSelected;
      const ratesA = taskObjectRates[a] || [];
      const ratesB = taskObjectRates[b] || [];
      const allObjs = [...new Set([...ratesA, ...ratesB].map((o) => o.name))];
      const mapA = Object.fromEntries(ratesA.map((o) => [o.name, o.rate]));
      const mapB = Object.fromEntries(ratesB.map((o) => [o.name, o.rate]));

      allObjs.sort((x, y) => {
        const dA = mapA[x] != null && mapB[x] != null ? mapA[x] - mapB[x] : null;
        const dB = mapA[y] != null && mapB[y] != null ? mapA[y] - mapB[y] : null;
        if (dB === null && dA === null) return 0;
        if (dB === null) return -1;
        if (dA === null) return 1;
        return dB - dA;
      });

      const maxDelta = 100;
      const rows = allObjs
        .map((obj) => {
          const rA = mapA[obj] ?? null;
          const rB = mapB[obj] ?? null;
          const delta = rA !== null && rB !== null ? rA - rB : null;
          const pct = delta === null ? 0 : (Math.abs(delta) / maxDelta) * 100;
          const aWins = delta !== null && delta > 0.5;
          const bWins = delta !== null && delta < -0.5;
          const tied = delta !== null && !aWins && !bWins;
          const deltaStr = delta === null ? '' : `+${Math.abs(delta).toFixed(1)}%`;

          const leftContent = bWins
            ? `<span class="compare-delta-pct" style="background:rgba(96,165,250,0.15);color:#60a5fa">${deltaStr}</span><div class="compare-delta-fill" style="width:${pct}%;background:linear-gradient(90deg,rgba(96,165,250,0.5),#60a5fa)"></div>`
            : tied
              ? `<span style="color:var(--text-muted);font-size:0.75rem;font-family:'Manrope',sans-serif">~</span>`
              : '';
          const rightContent = aWins
            ? `<div class="compare-delta-fill" style="width:${pct}%;background:linear-gradient(90deg,var(--accent),rgba(240,82,156,0.5))"></div><span class="compare-delta-pct" style="background:rgba(240,82,156,0.15);color:var(--accent)">${deltaStr}</span>`
            : '';

          return `<div class="compare-delta-row">
          <div class="compare-delta-bar-left">${leftContent}</div>
          <span class="compare-delta-name">${obj}</span>
          <div class="compare-delta-bar-right">${rightContent}</div>
        </div>`;
        })
        .join('');

      const compareCols = root.querySelector('#compare-cols');
      if (compareCols) {
        compareCols.innerHTML = `
        <div style="font-family:'Manrope',sans-serif;font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--text-muted);text-align:center;margin-bottom:10px;">Success Rate Difference (%)</div>
        <div class="compare-delta-header">
          <div style="display:flex;justify-content:flex-end"><span class="hdr-policy" style="background:rgba(96,165,250,0.12);color:#60a5fa">\u2190 ${b}</span></div>
          <div class="hdr-center">Object</div>
          <div><span class="hdr-policy" style="background:rgba(240,82,156,0.12);color:var(--accent)">${a} \u2192</span></div>
        </div>
        ${rows}`;
      }

      const overlay = root.querySelector('#compare-modal-overlay') as HTMLElement;
      if (overlay) overlay.classList.add('visible');
    }

    function closeCompareModal(event: Event | null, force?: boolean) {
      const overlay = root.querySelector('#compare-modal-overlay') as HTMLElement;
      if (force || !event || event.target === overlay) {
        if (overlay) overlay.classList.remove('visible');
        compareMode = false;
        compareSelected = [];
        const btn = root.querySelector('#compare-btn') as HTMLElement;
        if (btn) btn.classList.remove('active');
        renderTable();
      }
    }

    function toggleGeneralistFilter() {
      filterGeneralists = !filterGeneralists;
      const btn = root.querySelector('#filter-generalist-btn') as HTMLElement;
      if (btn) btn.classList.toggle('active', filterGeneralists);
      const table = root.querySelector('#leaderboard') as HTMLElement;
      if (table) table.classList.toggle('hide-gen-col', filterGeneralists);
      renderTable();
      renderChart();
    }

    function toggleNonIDFilter() {
      filterNonID = !filterNonID;
      const btn = root.querySelector('#filter-non-id-btn') as HTMLElement;
      if (btn) btn.classList.toggle('active', filterNonID);
      const table = root.querySelector('#leaderboard') as HTMLElement;
      if (table) table.classList.toggle('hide-col-data_source', filterNonID);
      const table2 = root.querySelector('#leaderboard-2') as HTMLElement;
      if (table2) table2.classList.toggle('hide-col-data_source', filterNonID);
      renderTable();
      renderChart();
    }

    function resetFilters() {
      filterGeneralists = false;
      filterNonID = true;
      colFilters = {};
      const genBtn = root.querySelector('#filter-generalist-btn') as HTMLElement;
      if (genBtn) genBtn.classList.remove('active');
      const nonIdBtn = root.querySelector('#filter-non-id-btn') as HTMLElement;
      if (nonIdBtn) nonIdBtn.classList.add('active');
      Object.keys(COL_FILTER_DEFS).forEach((key) => updateColFilterBtn(key));
      const resetBtn = root.querySelector('#reset-filters-btn') as HTMLElement;
      if (resetBtn) resetBtn.classList.remove('active');
      renderTable();
      renderChart();
    }

    function fmtDate(d: string) {
      if (!d) return '\u2014';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    }

    function getActionClass(space: string) {
      const s = (space || '').toLowerCase();
      if (s.includes('cartesian') || s.includes('cart.') || s.includes('eef')) return 'pill-action-cartesian';
      if (s.includes('joint')) return 'pill-action-joint';
      if (s.includes('end-effector') || s.includes('end effector') || s.includes('ee '))
        return 'pill-action-ee';
      return 'pill-action';
    }

    function renderBooleanBadge(value: string, withLabel = false) {
      if (value === '—') return '<span style="color:var(--text-muted)">—</span>';
      const yes = (value || '').toLowerCase() === 'yes';
      return `<span class="${yes ? 'badge-yes' : 'badge-no'}">${yes ? (withLabel ? '✓ Yes' : '✓') : (withLabel ? '\u2717 No' : '\u2717')}</span>`;
    }

    function getAffilLogos(affil: string) {
      return (affil || '')
        .split(/[&/]/)
        .map((a) => a.trim())
        .filter(Boolean)
        .map((a) =>
          logos[a]
            ? `<img src="${logos[a]}" alt="${a}" class="affiliation-logo" title="${a}" onerror="this.style.display='none'">`
            : ''
        )
        .join('');
    }

    function parseCSV(text: string) {
      const lines = text
        .trim()
        .split('\n')
        .filter((l) => !l.trimStart().startsWith('#'));
      if (lines.length < 2) return [];
      const headers = parseCSVLine(lines[0]);
      const rows: Record<string, string>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = parseCSVLine(lines[i]);
        if (vals.length < headers.length) continue;
        if (!vals[0].trim()) continue;
        const row: Record<string, string> = {};
        headers.forEach((h, idx) => {
          row[h.trim()] = (vals[idx] || '').trim();
        });
        rows.push(row);
      }
      return rows;
    }

    function parseCSVLine(line: string) {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const c = line[i];
        if (c === '"') {
          inQuotes = !inQuotes;
        } else if (c === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += c;
        }
      }
      result.push(current);
      return result;
    }

    function getSuccessNum(val: string) {
      if (!val) return -1;
      return parseFloat(val.replace('%', '')) || 0;
    }

    function getDataClass(data: string) {
      const d = (data || '').toLowerCase();
      if (d === 'real') return 'data-real';
      if (d === 'sim') return 'data-sim';
      if (d === 'mixed') return 'data-mixed';
      if (d === 'id') return 'data-id';
      return '';
    }

    function getTypeClass(type: string) {
      const t = type.toLowerCase();
      if (t === 'vla') return 'type-vla';
      if (t === 'wam') return 'type-wam';
      if (t.includes('tamp')) return 'type-tamp';
      if (t.includes('bm') || t.includes('vlm')) return 'type-bm-vlm';
      return 'type-default';
    }

    function getEmbodimentClass(embodiment: string) {
      if ((embodiment || '').toLowerCase().includes('cap')) return 'embodiment-cap';
      return 'embodiment-droid';
    }

    function getBarColor(pct: number) {
      if (pct >= 50) return '#22c55e';
      if (pct >= 30) return '#eab308';
      if (pct >= 15) return '#f97316';
      return '#ef4444';
    }

    function sortData(data: Record<string, string>[]) {
      return [...data].sort((a, b) => {
        let va: number | string, vb: number | string;
        if (sortCol === 'oracle_rate') {
          va = getSuccessNum(a['Oracle Rate']);
          vb = getSuccessNum(b['Oracle Rate']);
        } else if (sortCol === 'success_rate') {
          va = getSuccessNum(a['Success Rate']);
          vb = getSuccessNum(b['Success Rate']);
        } else if (sortCol === 'smoothness') {
          va = parseFloat((a.Smoothness || '').split('\u00b1')[0]) || Infinity;
          vb = parseFloat((b.Smoothness || '').split('\u00b1')[0]) || Infinity;
        } else if (sortCol === 'gen_tasks') {
          va = (a['Gen Tasks'] || '').toLowerCase() === 'yes' ? 1 : 0;
          vb = (b['Gen Tasks'] || '').toLowerCase() === 'yes' ? 1 : 0;
        } else if (sortCol === 'type') {
          va = a.Type || '';
          vb = b.Type || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        } else if (sortCol === 'chunk_size') {
          va = parseInt(a['Chunk Size']) || 0;
          vb = parseInt(b['Chunk Size']) || 0;
        } else if (sortCol === 'policy') {
          va = a.Policy || '';
          vb = b.Policy || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        } else if (sortCol === 'affiliation') {
          va = a.Affiliation || '';
          vb = b.Affiliation || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        } else if (sortCol === 'date_tested') {
          va = new Date(a['Date Tested'] || '1970-01-01').getTime();
          vb = new Date(b['Date Tested'] || '1970-01-01').getTime();
        } else if (sortCol === 'date_released') {
          va = new Date(a['Date Released'] || '1970-01-01').getTime();
          vb = new Date(b['Date Released'] || '1970-01-01').getTime();
        } else if (sortCol === 'action_space') {
          va = a['Action Space'] || '';
          vb = b['Action Space'] || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        } else if (sortCol === 'open_sourced' || sortCol === 'model_open_sourced') {
          va = (a['Open Sourced'] || '').toLowerCase() === 'yes' ? 1 : 0;
          vb = (b['Open Sourced'] || '').toLowerCase() === 'yes' ? 1 : 0;
        } else if (sortCol === 'data_open_sourced') {
          va = (a.Policy || '').includes('\u03c0') ? 0 : 1;
          vb = (b.Policy || '').includes('\u03c0') ? 0 : 1;
        } else if (sortCol === 'embodiment') {
          va = a.Embodiment || '';
          vb = b.Embodiment || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        } else {
          va = a[sortCol] || '';
          vb = b[sortCol] || '';
          return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
        }
        return sortDir === 'asc' ? va - vb : vb - va;
      });
    }

    function getObjectRates(policy: string) {
      return taskObjectRates[policy] || [];
    }

    function getObjColor(pct: number) {
      if (pct >= 50) return 'var(--green)';
      if (pct >= 30) return '#b8720d';
      if (pct >= 15) return '#d97706';
      return 'var(--red)';
    }

    function toggleDetail(rowId: number) {
      const detail = root.querySelector('#detail-' + rowId) as HTMLElement;
      const icon = root.querySelector('#expand-' + rowId) as HTMLElement;
      if (!detail || !icon) return;
      if (detail.style.display === 'none') {
        detail.style.display = 'table-row';
        icon.classList.add('open');
      } else {
        detail.style.display = 'none';
        icon.classList.remove('open');
      }
    }

    function exportCSV() {
      let displayData = filterGeneralists
        ? currentData.filter((r) => (r['Gen Tasks'] || '').toLowerCase() === 'yes')
        : currentData;
      const sorted = sortData(displayData);
      if (!sorted.length) return;

      const cols = [
        'Policy',
        'Affiliation',
        'Type',
        'Parameters (B)',
        'Data Source',
        'Data Type',
        'Success Rate',
        'CI',
        'Smoothness',
        'Robot Generalist',
        'Gen Tasks',
        'Gen Environments',
        'Gen Embodiment',
        'Open Sourced',
        'Action Space',
        'Paper',
        'Date Released',
      ];

      const esc = (v: string) => {
        const s = String(v ?? '').replace(/"/g, '""');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
      };

      const lines = [cols.map(esc).join(',')];
      sorted.forEach((row) => {
        lines.push(cols.map((c) => esc(row[c] ?? '')).join(','));
      });

      const task = root.querySelector('#task') as HTMLSelectElement;
      const taskLabel = task?.options[task.selectedIndex]?.text || 'leaderboard';
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `molmospaces_${taskLabel.replace(/\s+/g, '_').toLowerCase()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }

    function renderTable() {
      hideLoading();
      const tbody = root.querySelector('#leaderboard-body') as HTMLElement;
      if (!tbody) return;
      // Ensure column visibility matches current state on both tables
      const _lb = root.querySelector('#leaderboard') as HTMLElement;
      const _lb2 = root.querySelector('#leaderboard-2') as HTMLElement;
      [_lb, _lb2].forEach((t) => {
        if (!t) return;
        t.classList.toggle('hide-col-data_source', filterNonID);
        t.classList.toggle('show-oracle-rate', !!currentTaskFlags.showOracleRate);
        t.classList.toggle('hide-success-rate', !!currentTaskFlags.oracleOnly);
        t.classList.toggle('show-task-count', !!currentTaskFlags.showTaskCount);
      });
      const tbody2 = root.querySelector('#leaderboard-body-2') as HTMLElement;
      const gh1 = root.querySelector('#leaderboard-gh-1') as HTMLElement;
      const gh2 = root.querySelector('#leaderboard-gh-2') as HTMLElement;
      const lb2container = root.querySelector('#leaderboard-2-container') as HTMLElement;
      const lb2thead = root.querySelector('#leaderboard-2-thead') as HTMLElement;
      const lb1thead = root.querySelector('#leaderboard thead') as HTMLElement;

      if (currentData.length === 0) {
        tbody.innerHTML = `
          <tr><td colspan="15">
            <div class="empty-state">
              <p>No data available for this task</p>
              <small>Check back later or add entries to the CSV</small>
            </div>
          </td></tr>`;
        updateSortHeaders();
        return;
      }

      let displayData = filterGeneralists
        ? currentData.filter((r) => (r['Gen Tasks'] || '').toLowerCase() === 'yes')
        : currentData;
      Object.entries(colFilters).forEach(([colKey, allowed]) => {
        if (!allowed || allowed.size === 0) return;
        const def = COL_FILTER_DEFS[colKey];
        if (!def) return;
        displayData = displayData.filter((r) => allowed.has(def.extract(r)));
      });
      let html = '';

      function parseCIBounds(row: Record<string, string>) {
        const ci = row.CI;
        if (!ci || ci === '\u2014') return null;
        const parts = ci.split('\u2013');
        if (parts.length !== 2) return null;
        const lo = parseFloat(parts[0].replace('%', ''));
        const hi = parseFloat(parts[1].replace('%', ''));
        return isNaN(lo) || isNaN(hi) ? null : { lo, hi };
      }

      function computeRanks(rows: Record<string, string>[]) {
        const ranks: number[] = [];
        for (let i = 0; i < rows.length; i++) {
          if (i === 0) {
            ranks.push(1);
          } else {
            const prevBounds = parseCIBounds(rows[i - 1]);
            const curBounds = parseCIBounds(rows[i]);
            const tied =
              sortCol === 'success_rate' &&
              prevBounds &&
              curBounds &&
              prevBounds.lo <= curBounds.hi &&
              curBounds.lo <= prevBounds.hi;
            ranks.push(tied ? ranks[i - 1] : i + 1);
          }
        }
        return ranks;
      }

      let groups: { label: string | null; rows: Record<string, string>[] }[];
      if (filterNonID) {
        const nonID = sortData(displayData.filter((r) => (r['Data Source'] || '').toLowerCase() !== 'id'));
        const id = sortData(displayData.filter((r) => (r['Data Source'] || '').toLowerCase() === 'id'));
        groups = [
          { label: 'Not Using MolmoSpaces Data', rows: nonID },
          { label: 'Using MolmoSpaces Data', rows: id },
        ];
      } else {
        groups = [{ label: null, rows: sortData(displayData) }];
      }

      renderedRows = groups.flatMap((g) => g.rows);
      let globalIdx = 0;
      let html2 = '';
      groups.forEach(({ rows }, groupIdx) => {
        const ranks = computeRanks(rows);
        rows.forEach((row, i) => {
          const idx = globalIdx++;
          const rank = ranks[i];
          const isGroup2 = filterNonID && groupIdx === 1;
        const pct = getSuccessNum(row['Oracle Rate'] || row['Success Rate']);
        const barColor = getBarColor(pct);
        const rankClass = rank <= 3 ? `rank rank-${rank}` : 'rank rank-other';
        const affil = row.Affiliation || '';
        const logoUrl = logos[affil];
        const logoHtml = logoUrl
          ? `<img src="${logoUrl}" alt="${affil}" class="affiliation-logo" onerror="this.style.display='none'">`
          : getAffilLogos(affil);
        const policyName = row.Policy || '';
        const isCompareSelected = compareSelected.includes(policyName);
        const rowClass =
          'data-row' + (compareMode && isCompareSelected ? ' compare-row-selected' : '');

        const rowHtmlStart = `<tr class="${rowClass}" data-row-idx="${idx}" style="animation-delay:${idx * 30}ms;cursor:pointer">
          <td><span class="${compareMode ? 'compare-check-box' : 'expand-icon'}" id="expand-${idx}">${compareMode ? '✓' : '&#9654;'}</span><span class="${rankClass}">${rank}</span></td>
          <td>
            <span class="policy-cell">
              <span class="policy-name">${(row.Policy || '').replace(/([\u2080\u2081\u2082\u2083\u2084\u2085\u2086\u2087\u2088\u2089\u208A\u208B\u208C\u208D\u208E]+)/g, '<span style="font-size:0.9em;vertical-align:baseline">$1</span>')}</span>${(() => {
                const dateStr = row['Date Released'] || '';
                if (!dateStr || dateStr === '\u2014') return '';
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return '';
                const daysAgo = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
                return (daysAgo >= 0 && daysAgo <= 90) ? '<span class="new-badge" title="Added within the past 3 months">NEW</span>' : '';
              })()}
            </span>
          </td>
          <td>
            <span class="policy-cell">
              ${logoHtml}
              <span style="font-size:0.85rem;font-weight:500">${affil || '\u2014'}</span>
            </span>
          </td>
          <td class="td-center" data-col="oracle_rate">
            <div class="success-cell">
              <span class="success-value" style="color:${barColor}">
                ${(row['Oracle Rate'] || '\u2014').replace('%','')} ${(() => {
                  const ci = row['Oracle CI'];
                  if (!ci || ci === '\u2014') return '';
                  const parts = ci.split('\u2013');
                  if (parts.length !== 2) return `<span class="ci-value">[${ci}]</span>`;
                  const or = parseFloat((row['Oracle Rate'] || '0').replace('%', ''));
                  const lo = parseFloat(parts[0].replace('%', ''));
                  const hi = parseFloat(parts[1].replace('%', ''));
                  const margin = Math.max(or - lo, hi - or);
                  return `<span class="ci-value">\u00b1${margin.toFixed(1)}</span>`;
                })()}
              </span>
              <div class="success-bar-bg">
                <div class="success-bar" style="width: ${Math.max(pct, 0)}%; background: linear-gradient(90deg, ${barColor}99, ${barColor});"></div>
              </div>
            </div>
          </td>
          ${!currentTaskFlags.oracleOnly ? `<td data-col="success_rate">
            <div class="success-cell">
              <span class="success-value" style="color: ${getBarColor(getSuccessNum(row['Success Rate']))}">${(row['Success Rate'] || '\u2014').replace('%', '')} ${(() => {
                const ci = row.CI;
                if (!ci || ci === '\u2014') return '';
                const parts = ci.split('\u2013');
                if (parts.length !== 2) return `<span class="ci-value">[${ci}]</span>`;
                const sr = parseFloat((row['Success Rate'] || '0').replace('%', ''));
                const lo = parseFloat(parts[0].replace('%', ''));
                const hi = parseFloat(parts[1].replace('%', ''));
                const margin = (hi - lo) / 2;
                return `<span class="ci-value">\u00b1${margin.toFixed(1)}</span>`;
              })()}</span>
              <div class="success-bar-bg">
                <div class="success-bar" style="width: ${Math.max(getSuccessNum(row['Success Rate']), 0)}%; background: linear-gradient(90deg, ${getBarColor(getSuccessNum(row['Success Rate']))}99, ${getBarColor(getSuccessNum(row['Success Rate']))});"></div>
              </div>
            </div>
          </td>` : ''}
          <td class="td-center" data-col="task_count">${row['Task Count'] || '\u2014'}</td>
          <td class="td-center" data-col="data_source"><span class="${(row['Data Source'] || '').toLowerCase() === 'id' ? 'badge-yes' : 'badge-no'}">${(row['Data Source'] || '').toLowerCase() === 'id' ? '✓' : '\u2717'}</span></td>
          <td class="td-center gen-tasks-td" data-col="gen_tasks">${renderBooleanBadge(row['Gen Tasks'], false)}</td>
          <td class="td-center" data-col="robot_generalist">${(() => { const rg = (row['Robot Generalist'] || '').trim(); if (rg === '—') return renderBooleanBadge(rg); if (rg === '~') return `<span style="color:var(--text-muted)">\u007e</span>`; return `<span class="${rg.toLowerCase() === 'yes' ? 'badge-yes' : 'badge-no'}">${rg.toLowerCase() === 'yes' ? '✓' : '\u2717'}</span>`; })()}</td>
          <td class="td-center" data-col="requires_planning">${renderBooleanBadge(row['Needs Warmup Planning'], false)}</td>
          <td class="td-center" data-col="data_type"><span class="pill ${getDataClass(row['Data Type'] || '')}">${(row['Data Type'] || '').toLowerCase() === 'id' ? 'In Dist.' : (row['Data Type'] || '\u2014')}</span></td>
          ${(() => {
            const _isPi = (row.Policy || '').includes('\u03c0');
            const _dataNA = (row['Data Source'] || '').trim() === '—' || (row['Data Source'] || '').trim() === '';
            const _dataOpenMeta = (row['Data Open Sourced'] || '').toLowerCase();
            const _dataOpen = _dataOpenMeta ? _dataOpenMeta === 'yes' : !_isPi;
            const _dataCell = _dataNA
              ? `<span style="color:var(--text-muted)">—</span>`
              : `<span class="${_dataOpen ? 'badge-yes' : 'badge-no'}">${_dataOpen ? '✓' : '\u2717'}</span>`;
            return `<td class="td-center" data-col="data_open_sourced">${_dataCell}</td><td class="td-center" data-col="model_open_sourced">${renderBooleanBadge(row['Open Sourced'])}</td>`;
          })()}
          <td class="td-center" data-col="paper">${row.Paper ? `<a href="${row.Paper}" class="video-link paper-icon" target="_blank" data-stop-propagation>🌐</a>` : `<a href="#" class="video-link paper-icon" data-stop-propagation>🌐</a>`}</td>
          <td class="td-center" data-col="type"><span class="pill ${getTypeClass(row.Type || '')}" title="Type">${row.Type || '\u2014'}</span></td>
          <td class="td-center" data-col="embodiment"><span class="pill ${getEmbodimentClass(row.Embodiment || '')}">${row.Embodiment || '\u2014'}</span></td>
          <td class="td-center" data-col="action_space"><span class="pill ${getActionClass(row['Action Space'])}">${(row['Action Space'] || '\u2014').replace(/^\d+-DoF\s*/i, '')}</span></td>
          <td class="td-center" data-col="parameters">${row['Parameters (B)'] || '\u2014'}</td>

          <td data-col="smoothness">
            <div class="success-cell">
              ${(() => {
                const j = row.Smoothness;
                const isCartesian = (row['Action Space'] || '').toLowerCase().includes('cartesian');
                if (!j || j === '\u2014')
                  return '<span class="success-value" style="color:var(--muted)">\u2014</span>';
                const mean = j.split('\u00b1')[0].trim();
                return `<span class="success-value">${mean}${isCartesian ? '<sup style="color:var(--text-muted);font-size:0.85em;margin-left:1px">*</sup>' : ''}</span>`;
              })()}
            </div>
          </td>
          <td class="date-cell" data-col="date_released">${fmtDate(row['Date Released'])}</td>
        </tr>`;

        if (isGroup2) html2 += rowHtmlStart; else html += rowHtmlStart;

        // Detail row
        const objRates = getObjectRates(row.Policy || '');
        const chips = objRates
          .map(
            (o) =>
              `<div class="object-chip"><span class="obj-name">${o.name}</span><span class="obj-rate" style="color: ${getObjColor(o.rate)}">${o.rate}%</span></div>`
          )
          .join('');
        const objectsSection =
          currentTaskFlags.showObjects && chips
            ? `<h4 style="margin-top:16px">Success Rate by Object</h4><div class="object-grid">${chips}</div>`
            : !currentTaskFlags.showObjects && row._file
            ? `<div style="margin-top:8px"><a href="/benchmark/data/${currentTaskId}/${row._file}.csv" class="video-link" target="_blank" data-stop-propagation>&#128196; Per-Object Results (CSV)</a></div>`
            : '';

        const detailHtml = `<tr class="detail-row" id="detail-${i}" style="display:none">
          <td colspan="${countVisibleCols()}">
            <div class="detail-content">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:0;margin-bottom:12px">
                <h4 style="margin:0">Additional Details</h4>
                <button class="filter-btn view-all-tasks-btn" data-policy-name="${(row.Policy || '').replace(/"/g, '&quot;')}" data-stop-propagation style="font-size:0.78rem;padding:6px 14px">📊 View All Tasks</button>
              </div>
              <div class="detail-info-grid">
                <div class="detail-info-item"><span class="detail-label">Cross-Embodiment</span><span class="detail-value">${renderBooleanBadge(row['Gen Embodiment'], true)}</span></div>
                <div class="detail-info-item"><span class="detail-label">Type</span><span class="detail-value"><span class="type-badge ${getTypeClass(row.Type || '')}">${row.Type || '\u2014'}</span></span></div>
                <div class="detail-info-item"><span class="detail-label">Parameters</span><span class="detail-value">${row['Parameters (B)'] || '\u2014'}</span></div>
                <div class="detail-info-item"><span class="detail-label">Open Sourced</span><span class="detail-value">${renderBooleanBadge(row['Open Sourced'], true)}</span></div>
                <div class="detail-info-item"><span class="detail-label">Paper</span><span class="detail-value">${(row.ArXiv || row.Paper) ? `<a href="${row.ArXiv || row.Paper}" class="video-link" target="_blank" data-stop-propagation>&#128196; Paper</a>` : `<a href="#" class="video-link" data-stop-propagation>&#128196; Paper</a>`}</span></div>
                <div class="detail-info-item"><span class="detail-label">Results Logs</span><span class="detail-value">${(row['Results Logs'] && row['Results Logs'] !== '—') ? `<a href="${row['Results Logs']}" class="video-link" target="_blank" data-stop-propagation>&#128203; View Logs</a>` : '—'}</span></div>
                <div class="detail-info-item"><span class="detail-label">Action Space</span><span class="detail-value">${row['Action Space'] || '\u2014'}</span></div>
                <div class="detail-info-item"><span class="detail-label">Chunk Size</span><span class="detail-value">${row['Chunk Size'] || '\u2014'}</span></div>
                <div class="detail-info-item"><span class="detail-label">Context Length</span><span class="detail-value">${row['Context Length'] || '\u2014'}</span></div>
                <!--
                <div class="detail-info-item"><span class="detail-label">Videos</span><span class="detail-value">${row['Videos Link'] ? `<a href="#" class="video-link" data-stop-propagation>${row['Videos Link']}</a>` : '\u2014'}</span></div>
                -->
                <div class="detail-info-item"><span class="detail-label">Date Tested</span><span class="detail-value">${row['Date Tested'] || '\u2014'}</span></div>
                <div class="detail-info-item"><span class="detail-label">Date Released</span><span class="detail-value">${row['Date Released'] || '\u2014'}</span></div>
              </div>
              ${(row['Action Space'] || '').toLowerCase().includes('cartesian') ? `<p style="margin-top:12px;font-size:0.76rem;color:var(--text-muted);font-family:'Lato',sans-serif;line-height:1.5"><sup>*</sup> Joint jerk is computed from joint trajectories produced by the IK solver, not directly from the policy output, since this policy operates in Cartesian end-effector space.</p>` : ''}
              ${objectsSection}
            </div>
          </td>
        </tr>`;
          if (isGroup2) html2 += detailHtml; else html += detailHtml;
        }); // rows.forEach
      }); // groups.forEach

      // Show/hide split elements and populate tbodies
      if (filterNonID) {
        const hasNonID = groups[0].rows.length > 0;
        const hasID = groups[1].rows.length > 0;
        if (gh1) gh1.style.display = hasNonID ? '' : 'none';
        if (gh2) gh2.style.display = hasID ? '' : 'none';
        if (lb2container) lb2container.style.display = hasID ? '' : 'none';
        if (lb2thead && lb1thead) lb2thead.innerHTML = lb1thead.innerHTML;
        if (tbody2) tbody2.innerHTML = html2;
        const lb2 = root.querySelector('#leaderboard-2') as HTMLElement;
        if (lb2) {
          lb2.classList.toggle('show-oracle-rate', currentTaskFlags.showOracleRate);
          lb2.classList.toggle('hide-success-rate', !!currentTaskFlags.oracleOnly);
          lb2.classList.toggle('show-task-count', !!currentTaskFlags.showTaskCount);
          lb2.classList.toggle('hide-col-data_source', filterNonID);
        }
        // Sync column widths from table 1 to table 2 (skip hidden columns)
        setTimeout(() => {
          const ths1 = Array.from(root.querySelectorAll('#leaderboard thead tr:first-child th')) as HTMLElement[];
          const ths2 = Array.from(root.querySelectorAll('#leaderboard-2 thead tr:first-child th')) as HTMLElement[];
          ths1.forEach((th, i) => {
            if (ths2[i] && th.offsetWidth > 0) ths2[i].style.width = th.offsetWidth + 'px';
            else if (ths2[i]) ths2[i].style.width = '';
          });
          if (lb2) lb2.style.tableLayout = '';
        }, 0);
      } else {
        if (gh1) gh1.style.display = 'none';
        if (gh2) gh2.style.display = 'none';
        if (lb2container) lb2container.style.display = 'none';
      }

      tbody.innerHTML = html;
      updateSortHeaders();
      applyHiddenCols();
      const lbTable = root.querySelector('#leaderboard') as HTMLElement;
      if (lbTable) {
        lbTable.classList.toggle('show-oracle-rate', currentTaskFlags.showOracleRate);
        lbTable.classList.toggle('hide-success-rate', !!currentTaskFlags.oracleOnly);
        lbTable.classList.toggle('show-task-count', !!currentTaskFlags.showTaskCount);
      }
      const srTh = root.querySelector('#leaderboard th[data-col="success_rate"]');
      if (srTh) srTh.firstChild!.textContent = currentTaskFlags.oracleLabel ? 'Oracle Success Rate' : 'End Success Rate';

      // Attach click handlers via delegation
      const clickTargets = [tbody, ...(filterNonID && tbody2 ? [tbody2] : [])];
      clickTargets.forEach((tgt) => tgt.querySelectorAll('tr.data-row').forEach((tr) => {
        tr.addEventListener('click', (e) => {
          // Don't handle clicks on links
          if ((e.target as HTMLElement).closest('[data-stop-propagation]')) {
            e.stopPropagation();
            return;
          }
          const idx = parseInt((tr as HTMLElement).dataset.rowIdx || '0', 10);
          if (compareMode) {
            selectForCompare(idx);
          } else {
            toggleDetail(idx);
          }
        });
      })); // clickTargets

      // Delegated: "View All Tasks" button in expanded detail rows
      clickTargets.forEach((tgt) => {
        tgt.querySelectorAll('.view-all-tasks-btn').forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const policyName = (btn as HTMLElement).dataset.policyName || '';
            openPolicyTasksModal(policyName);
          });
        });
      });
    }

    async function openPolicyTasksModal(policyName: string) {
      const overlay = root.querySelector('#policy-tasks-overlay') as HTMLElement;
      const titleEl = root.querySelector('#policy-tasks-title') as HTMLElement;
      const subEl = root.querySelector('#policy-tasks-subtitle') as HTMLElement;
      const bodyEl = root.querySelector('#policy-tasks-body') as HTMLElement;
      if (!overlay || !tasksList) return;

      titleEl.innerHTML = `All Task Results — <span style="color:var(--accent)">${policyName}</span>`;
      subEl.textContent = 'Loading…';
      bodyEl.innerHTML = '';
      overlay.classList.add('visible');

      const eligibleTasks = tasksList.filter((t) => !t.combined && t.id !== 'ms_nav_multitype');
      const rows: { taskLabel: string; oracle: string; end: string; hasOracle: boolean }[] = [];

      await Promise.all(eligibleTasks.map(async (task) => {
        try {
          const metaResp = await fetch(`/benchmark/data/${task.id}/metadata.csv`);
          const metaRows = parseCSV(await metaResp.text());
          const meta = metaRows.find((r) => (r.Policy || '') === policyName);
          if (!meta) return;
          const file = meta.file;
          const csvResp = await fetch(`/benchmark/data/${task.id}/${file}.csv`);
          const text = await csvResp.text();
          const lines = text.trim().split('\n').filter((l) => !l.trimStart().startsWith('#') && l.trim());
          if (lines.length < 2) return;
          const headers = parseCSVLine(lines[0]);
          for (let i = 1; i < lines.length; i++) {
            const vals = parseCSVLine(lines[i]);
            if (vals.length < headers.length) continue;
            const rowMap: Record<string, string> = {};
            headers.forEach((h, idx) => { rowMap[h.trim()] = (vals[idx] || '').trim(); });
            if (rowMap.category !== 'OVERALL') continue;
            const oracle = rowMap.oracle_rate_pct || rowMap.success_rate_pct || '';
            const end = rowMap.oracle_rate_pct ? (rowMap.success_rate_pct || '') : '';
            rows.push({
              taskLabel: task.label_short || task.label,
              oracle: oracle ? parseFloat(oracle).toFixed(1) + '%' : '—',
              end: end ? parseFloat(end).toFixed(1) + '%' : '—',
              hasOracle: !!rowMap.oracle_rate_pct,
            });
            break;
          }
        } catch { /* skip */ }
      }));

      if (!rows.length) {
        subEl.textContent = 'No results found for this policy.';
        return;
      }

      const anyEnd = rows.some((r) => r.end !== '—' && r.end !== '');
      subEl.textContent = `${rows.length} task${rows.length === 1 ? '' : 's'} evaluated`;

      const headers = anyEnd ? ['Task', 'Oracle', 'End'] : ['Task', 'Oracle'];
      const tableHtml = `
        <div style="border:1px solid var(--border);border-radius:10px;overflow:hidden">
          <table style="width:100%;border-collapse:collapse;font-size:0.9rem">
            <thead>
              <tr>${headers.map((h) => `<th style="text-align:left;padding:10px 14px;background:var(--surface2);font-family:'Manrope',sans-serif;font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:0.8px;color:var(--text-muted);border-bottom:1px solid var(--border)">${h}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map((r, i) => `<tr style="${i < rows.length - 1 ? 'border-bottom:1px solid rgba(30,95,101,0.25)' : ''}">
                <td style="padding:10px 14px;font-weight:600">${r.taskLabel}</td>
                <td style="padding:10px 14px;font-variant-numeric:tabular-nums;font-weight:700">${r.oracle}</td>
                ${anyEnd ? `<td style="padding:10px 14px;font-variant-numeric:tabular-nums">${r.end || '—'}</td>` : ''}
              </tr>`).join('')}
            </tbody>
          </table>
        </div>`;
      bodyEl.innerHTML = tableHtml;
    }

    function updateSortHeaders() {
      root.querySelectorAll('thead th').forEach((th) => {
        th.classList.remove('sort-asc', 'sort-desc');
        if ((th as HTMLElement).dataset.col === sortCol) {
          th.classList.add(sortDir === 'asc' ? 'sort-asc' : 'sort-desc');
        }
      });
    }

    const _chartColors = [
      'rgba(240,82,156,0.5)', 'rgba(99,102,241,0.5)', 'rgba(217,119,6,0.5)',
      'rgba(22,163,74,0.5)', 'rgba(8,145,178,0.5)', 'rgba(220,38,38,0.5)',
      'rgba(124,58,237,0.5)', 'rgba(234,88,12,0.5)', 'rgba(5,150,105,0.5)',
      'rgba(100,116,139,0.5)',
    ];
    const _chartBorderColors = [
      'rgba(240,82,156,0.9)', 'rgba(99,102,241,0.9)', 'rgba(217,119,6,0.9)',
      'rgba(22,163,74,0.9)', 'rgba(8,145,178,0.9)', 'rgba(220,38,38,0.9)',
      'rgba(124,58,237,0.9)', 'rgba(234,88,12,0.9)', 'rgba(5,150,105,0.9)',
      'rgba(100,116,139,0.9)',
    ];

    function buildChartOnCanvas(canvasId: string, data: Record<string, string>[], legendId: string | null, ref: { current: Chart | null }) {
      const canvas = root.querySelector(`#${canvasId}`) as HTMLCanvasElement;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      if (ref.current) { ref.current.destroy(); ref.current = null; }
      if (data.length === 0) return;

      const sorted = [...data].sort((a, b) =>
        new Date(a['Date Released'] || '1970-01-01').getTime() -
        new Date(b['Date Released'] || '1970-01-01').getTime()
      );

      const points = sorted
        .filter((row) => {
          const p = row.Policy || '';
          if (p.toLowerCase().includes('paligemma')) return false;
          if (p.match(/CAP-Pick-[ABC]/i)) return false;
          if (filterGeneralists && (row['Gen Tasks'] || '').toLowerCase() !== 'yes') return false;
          return true;
        })
        .map((row) => ({
          x: new Date(row['Date Released'] || ''),
          y: parseFloat((row['Oracle Rate'] || row['Success Rate'] || '0').replace('%', '')) || 0,
          label: row.Policy || '',
        }))
        .filter((p) => !isNaN(p.x.getTime()));

      const frontierPoints: { x: Date; y: number; label: string }[] = [];
      let maxSoFar = -Infinity;
      points.forEach((p) => { if (p.y > maxSoFar) { maxSoFar = p.y; frontierPoints.push(p); } });

      ref.current = new Chart(ctx, {
        type: 'scatter',
        data: {
          datasets: [
            {
              data: points,
              backgroundColor: points.map((_, i) => _chartColors[i % _chartColors.length]),
              borderColor: points.map((_, i) => _chartBorderColors[i % _chartBorderColors.length]),
              pointRadius: 9, pointHoverRadius: 12, pointStyle: 'circle',
            },
            {
              type: 'line', data: frontierPoints,
              borderColor: 'rgba(240,82,156,0.75)', backgroundColor: 'transparent',
              borderWidth: 2, borderDash: [6, 4], pointRadius: 0, pointHoverRadius: 0,
              tension: 0, fill: false, order: -1,
            },
          ],
        },
        options: {
          responsive: true, maintainAspectRatio: false,
          layout: { padding: { top: 20, right: 10, bottom: 10, left: 10 } },
          scales: {
            x: {
              type: 'time', time: { unit: 'month', displayFormats: { month: 'MMM yyyy' } },
              title: { display: true, text: 'Release Date', font: { family: 'Manrope', size: 15, weight: '600' }, color: '#a8b8b0' },
              grid: { color: 'rgba(255,255,255,0.05)' },
              ticks: { color: '#a8b8b0', font: { family: 'Lato', size: 13 }, maxRotation: 45, minRotation: 45 },
            },
            y: {
              beginAtZero: true, max: 100,
              title: { display: true, text: 'Success Rate (%)', font: { family: 'Manrope', size: 15, weight: '600' }, color: '#a8b8b0' },
              grid: { color: 'rgba(255,255,255,0.05)' },
              ticks: { color: '#a8b8b0', font: { family: 'Lato', size: 13 } },
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#0A3235', titleColor: '#FAF2E9', bodyColor: '#FAF2E9',
              borderColor: '#1a5c61', borderWidth: 1,
              titleFont: { family: 'Manrope', weight: '700', size: 13 },
              bodyFont: { family: 'Lato', size: 12 },
              callbacks: {
                title: (items) => {
                  const item = items[0];
                  if (item.datasetIndex === 1) return frontierPoints[item.dataIndex]?.label || '';
                  return points[item.dataIndex]?.label || '';
                },
                label: (item) => {
                  const raw = item.raw as { x: Date; y: number };
                  return `${raw.y}% \u2014 ${raw.x.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
                },
              },
            },
          },
        },
      });

      if (legendId) {
        const legendEl = root.querySelector(`#${legendId}`);
        if (legendEl) {
          legendEl.innerHTML = points.map((p, i) =>
            `<div style="display:flex;align-items:center;gap:8px">
              <span style="flex-shrink:0;width:12px;height:12px;border-radius:50%;background:${_chartBorderColors[i % _chartBorderColors.length]}"></span>
              <span style="font-family:'Lato',sans-serif;font-size:0.78rem;color:var(--text);line-height:1.3">${p.label}</span>
            </div>`
          ).join('');
        }
      }
    }

    function renderChart() {
      if (chartRef2.current) { chartRef2.current.destroy(); chartRef2.current = null; }

      const chart2Container = root.querySelector('#progress-chart-2') as HTMLElement;
      const singleContainer = root.querySelector('#progress-chart-single') as HTMLElement;
      if (chart2Container) chart2Container.style.display = 'none';
      if (singleContainer) singleContainer.style.display = '';

      if (filterNonID) {
        // Single chart with two groups (two frontier lines, different point colors)
        const canvas = root.querySelector('#progressChart2') as HTMLCanvasElement;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }

        const filterRow = (row: Record<string, string>) => {
          const p = row.Policy || '';
          if (p.toLowerCase().includes('paligemma')) return false;
          if (p.match(/CAP-Pick-[ABC]/i)) return false;
          if (filterGeneralists && (row['Gen Tasks'] || '').toLowerCase() !== 'yes') return false;
          return true;
        };
        const toPoint = (row: Record<string, string>) => ({
          x: new Date(row['Date Released'] || ''),
          y: parseFloat((row['Oracle Rate'] || row['Success Rate'] || '0').replace('%', '')) || 0,
          label: row.Policy || '',
        });

        const nonIDData = currentData.filter((r) => (r['Data Source'] || '').toLowerCase() !== 'id');
        const idData = currentData.filter((r) => (r['Data Source'] || '').toLowerCase() === 'id');

        const nonIDPoints = nonIDData.filter(filterRow).map(toPoint).filter((p) => !isNaN(p.x.getTime()))
          .sort((a, b) => a.x.getTime() - b.x.getTime());
        const idPoints = idData.filter(filterRow).map(toPoint).filter((p) => !isNaN(p.x.getTime()))
          .sort((a, b) => a.x.getTime() - b.x.getTime());

        const buildFrontier = (pts: typeof nonIDPoints) => {
          const frontier: typeof pts = [];
          let max = -Infinity;
          pts.forEach((p) => { if (p.y > max) { max = p.y; frontier.push(p); } });
          return frontier;
        };

        const nonIDFrontier = buildFrontier(nonIDPoints);
        const idFrontier = buildFrontier(idPoints);

        const nonIDColor = 'rgba(99,102,241,';  // blue
        const idColor = 'rgba(240,82,156,';      // pink

        const allPoints = [...nonIDPoints, ...idPoints];
        const allLabels = allPoints.map((p) => p.label);
        const groupColors = [
          ...nonIDPoints.map(() => nonIDColor + '0.5)'),
          ...idPoints.map(() => idColor + '0.5)'),
        ];
        const groupBorders = [
          ...nonIDPoints.map(() => nonIDColor + '0.9)'),
          ...idPoints.map(() => idColor + '0.9)'),
        ];

        chartRef.current = new Chart(ctx, {
          type: 'scatter',
          data: {
            datasets: [
              {
                label: 'Not Using MolmoSpaces Data',
                data: nonIDPoints,
                backgroundColor: nonIDPoints.map((_, i) => _chartColors[i % _chartColors.length]),
                borderColor: nonIDPoints.map((_, i) => _chartBorderColors[i % _chartBorderColors.length]),
                pointRadius: 9, pointHoverRadius: 12, pointStyle: 'circle',
              },
              {
                label: 'Using MolmoSpaces Data',
                data: idPoints,
                backgroundColor: idPoints.map((_, i) => _chartColors[i % _chartColors.length]),
                borderColor: idPoints.map((_, i) => _chartBorderColors[i % _chartBorderColors.length]),
                pointRadius: 9, pointHoverRadius: 12, pointStyle: 'triangle',
              },
              {
                type: 'line', data: nonIDFrontier, label: 'Frontier (Non-MolmoBot)',
                borderColor: 'rgba(99,102,241,0.75)', backgroundColor: 'transparent',
                borderWidth: 2, borderDash: [6, 4], pointRadius: 0, pointHoverRadius: 0,
                tension: 0, fill: false, order: -1,
              },
              {
                type: 'line', data: idFrontier, label: 'Frontier (MolmoBot)',
                borderColor: 'rgba(240,82,156,0.75)', backgroundColor: 'transparent',
                borderWidth: 2, borderDash: [6, 4], pointRadius: 0, pointHoverRadius: 0,
                tension: 0, fill: false, order: -1,
              },
            ],
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            layout: { padding: { top: 20, right: 10, bottom: 10, left: 10 } },
            scales: {
              x: {
                type: 'time', time: { unit: 'month', displayFormats: { month: 'MMM yyyy' } },
                title: { display: true, text: 'Release Date', font: { family: 'Manrope', size: 15, weight: '600' }, color: '#a8b8b0' },
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#a8b8b0', font: { family: 'Lato', size: 13 }, maxRotation: 45, minRotation: 45 },
              },
              y: {
                beginAtZero: true, max: 100,
                title: { display: true, text: 'Success Rate (%)', font: { family: 'Manrope', size: 15, weight: '600' }, color: '#a8b8b0' },
                grid: { color: 'rgba(255,255,255,0.05)' },
                ticks: { color: '#a8b8b0', font: { family: 'Lato', size: 13 } },
              },
            },
            plugins: {
              legend: {
                display: true,
                labels: {
                  filter: (item) => item.datasetIndex < 2,
                  color: '#ffffff',
                  font: { family: 'Manrope', size: 12 },
                  usePointStyle: true,
                  generateLabels: () => {
                    return [
                      { text: 'Not Using MolmoSpaces Data', fillStyle: 'rgba(99,102,241,0.7)', strokeStyle: 'rgba(99,102,241,0.9)', fontColor: '#ffffff', pointStyle: 'circle', datasetIndex: 0 },
                      { text: 'Using MolmoSpaces Data', fillStyle: 'rgba(240,82,156,0.7)', strokeStyle: 'rgba(240,82,156,0.9)', fontColor: '#ffffff', pointStyle: 'triangle', datasetIndex: 1 },
                    ];
                  },
                },
              },
              tooltip: {
                backgroundColor: '#0A3235', titleColor: '#FAF2E9', bodyColor: '#FAF2E9',
                borderColor: '#1a5c61', borderWidth: 1,
                titleFont: { family: 'Manrope', weight: '700', size: 13 },
                bodyFont: { family: 'Lato', size: 12 },
                callbacks: {
                  title: (items) => {
                    const item = items[0];
                    if (item.datasetIndex === 0) return nonIDPoints[item.dataIndex]?.label || '';
                    if (item.datasetIndex === 1) return idPoints[item.dataIndex]?.label || '';
                    if (item.datasetIndex === 2) return nonIDFrontier[item.dataIndex]?.label || '';
                    return idFrontier[item.dataIndex]?.label || '';
                  },
                  label: (item) => {
                    const raw = item.raw as { x: Date; y: number };
                    return `${raw.y}% \u2014 ${raw.x.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
                  },
                },
              },
            },
          },
        });

        // Build legend
        const legendEl = root.querySelector('#progress-legend');
        if (legendEl) {
          const legendItems = [
            ...(nonIDPoints.length ? [`<div style="font-family:'Manrope',sans-serif;font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--text-muted);margin:4px 0">● Not Using MolmoSpaces Data</div>`] : []),
            ...nonIDPoints.map((p, i) => `<div style="display:flex;align-items:center;gap:8px">
              <span style="flex-shrink:0;width:12px;height:12px;border-radius:50%;background:${_chartBorderColors[i % _chartBorderColors.length]}"></span>
              <span style="font-family:'Lato',sans-serif;font-size:0.78rem;color:var(--text);line-height:1.3">${p.label}</span>
            </div>`),
            ...(idPoints.length ? [`<div style="font-family:'Manrope',sans-serif;font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:var(--text-muted);margin:8px 0 4px">▲ Using MolmoSpaces Data</div>`] : []),
            ...idPoints.map((p, i) => `<div style="display:flex;align-items:center;gap:8px">
              <span style="flex-shrink:0;width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-bottom:12px solid ${_chartBorderColors[i % _chartBorderColors.length]}"></span>
              <span style="font-family:'Lato',sans-serif;font-size:0.78rem;color:var(--text);line-height:1.3">${p.label}</span>
            </div>`),
          ];
          legendEl.innerHTML = legendItems.join('');
        }
      } else {
        buildChartOnCanvas('progressChart2', currentData, 'progress-legend', chartRef);
      }
    }

    async function loadCombined() {
      try {
        const regularTasks = tasksList!.filter((t) => !t.combined && t.id.startsWith('ms_') && t.id !== 'ms_nav_multitype');
        const numTasks = regularTasks.length;
        const policyMap: Record<
          string,
          {
            rates: number[];
            jerks: number[];
            meta: Record<string, string>;
          }
        > = {};

        await Promise.all(
          regularTasks.map(async (task) => {
            try {
              const metaResp = await fetch(`/benchmark/data/${task.id}/metadata.csv`);
              const metaText = await metaResp.text();
              const metaRows = parseCSV(metaText);
              const metaByFile: Record<string, Record<string, string>> = {};
              metaRows.forEach((r) => {
                metaByFile[r.file] = r;
              });

              await Promise.all(
                task.policies.map(async (file) => {
                  try {
                    const resp = await fetch(`/benchmark/data/${task.id}/${file}.csv`);
                    const text = await resp.text();
                    const lines = text
                      .trim()
                      .split('\n')
                      .filter((l) => !l.trimStart().startsWith('#') && l.trim());
                    if (lines.length < 2) return;
                    const headers = parseCSVLine(lines[0]);
                    const rows: Record<string, string>[] = [];
                    for (let i = 1; i < lines.length; i++) {
                      const vals = parseCSVLine(lines[i]);
                      if (vals.length < headers.length) continue;
                      const row: Record<string, string> = {};
                      headers.forEach((h, idx) => {
                        row[h.trim()] = (vals[idx] || '').trim();
                      });
                      rows.push(row);
                    }
                    const overall = rows.find((r) => r.category === 'OVERALL');
                    if (!overall) return;
                    const meta = metaByFile[file] || {};
                    const displayName = meta.Policy || file;
                    const sr = parseFloat(overall.oracle_rate_pct || overall.success_rate_pct) || 0;
                    const jerk = parseFloat(overall.jerk_joint_mean);
                    if (!policyMap[displayName]) {
                      policyMap[displayName] = {
                        rates: [],
                        jerks: [],
                        meta,
                      };
                    }
                    policyMap[displayName].rates.push(sr);
                    if (!isNaN(jerk)) policyMap[displayName].jerks.push(jerk);
                  } catch {
                    /* skip */
                  }
                })
              );
            } catch {
              /* skip */
            }
          })
        );

        const combined = Object.entries(policyMap)
          .filter(([, v]) => v.rates.length === numTasks)
          .map(([name, v]) => {
            const avg = v.rates.reduce((a, b) => a + b, 0) / v.rates.length;
            const avgJerk =
              v.jerks.length > 0
                ? (v.jerks.reduce((a, b) => a + b, 0) / v.jerks.length).toFixed(2)
                : '\u2014';
            const meta = v.meta;
            return {
              Policy: name,
              Affiliation: meta.Affiliation || '\u2014',
              'Success Rate': avg.toFixed(1) + '%',
              CI: '\u2014',
              Smoothness: avgJerk,
              Type: meta.Type || '\u2014',
              'Parameters (B)': meta['Parameters (B)'] || '\u2014',
              'Data Source': meta['Data Source'] || '\u2014',
              'Data Type': meta['Data Type'] || '\u2014',
              'Robot Generalist': meta['Robot Generalist'] || 'No',
              'Gen Tasks': meta['Gen Tasks'] || 'No',
              'Gen Environments': meta['Gen Environments'] || 'No',
              'Gen Embodiment': meta['Gen Embodiment'] || 'No',
              Embodiment: meta.Embodiment || '\u2014',
              'Open Sourced': meta['Open Sourced'] || 'No',
              'Data Open Sourced': meta['Data Open Sourced'] || '',
              'Needs Warmup Planning': meta['Needs Warmup Planning'] || 'No',
              'Results Logs': meta['Results Logs'] || '—',
              'Action Space': meta['Action Space'] || '\u2014',
              'Chunk Size': meta['Chunk Size'] || '\u2014',
              'Context Length': meta['Context Length'] || '\u2014',
              Paper: meta.Paper || '',
              ArXiv: meta.ArXiv || '',
              'Videos Link': meta['Videos Link'] || '',
              'Date Tested': meta['Date Tested'] || '',
              'Date Released': meta['Date Released'] || '',
            };
          });

        currentData = combined;
        taskObjectRates = {};
        sortCol = 'oracle_rate';
        sortDir = 'desc';
        renderTable();
        setTimeout(() => renderChart(), 50);
      } catch (e) {
        console.error('Failed to load combined:', e);
      }
    }

    async function loadCombinedMain() {
      try {
        const regularTasks = tasksList!.filter(
          (t) => !t.combined && t.id.startsWith('mb_')
        );
        const numTasks = regularTasks.length;
        const policyMap: Record<string, { rates: number[]; oracleRates: number[]; jerks: number[]; meta: Record<string, string> }> = {};

        await Promise.all(
          regularTasks.map(async (task) => {
            try {
              const metaResp = await fetch(`/benchmark/data/${task.id}/metadata.csv`);
              const metaText = await metaResp.text();
              const metaRows = parseCSV(metaText);
              const metaByFile: Record<string, Record<string, string>> = {};
              metaRows.forEach((r) => { metaByFile[r.file] = r; });

              await Promise.all(
                task.policies.map(async (file) => {
                  try {
                    const resp = await fetch(`/benchmark/data/${task.id}/${file}.csv`);
                    const text = await resp.text();
                    const lines = text.trim().split('\n').filter((l) => !l.trimStart().startsWith('#') && l.trim());
                    if (lines.length < 2) return;
                    const headers = parseCSVLine(lines[0]);
                    const rows: Record<string, string>[] = [];
                    for (let i = 1; i < lines.length; i++) {
                      const vals = parseCSVLine(lines[i]);
                      if (vals.length < headers.length) continue;
                      const row: Record<string, string> = {};
                      headers.forEach((h, idx) => { row[h.trim()] = (vals[idx] || '').trim(); });
                      rows.push(row);
                    }
                    const overall = rows.find((r) => r.category === 'OVERALL');
                    if (!overall) return;
                    const meta = metaByFile[file] || {};
                    const displayName = meta.Policy || file;
                    const sr = parseFloat(overall.success_rate_pct) || 0;
                    const oracleSr = parseFloat(overall.oracle_rate_pct || overall.success_rate_pct) || 0;
                    const jerk = parseFloat(overall.jerk_joint_mean);
                    if (!policyMap[displayName]) policyMap[displayName] = { rates: [], oracleRates: [], jerks: [], meta };
                    policyMap[displayName].rates.push(sr);
                    policyMap[displayName].oracleRates.push(oracleSr);
                    if (!isNaN(jerk)) policyMap[displayName].jerks.push(jerk);
                  } catch { /* skip */ }
                })
              );
            } catch { /* skip */ }
          })
        );

        const combined = Object.entries(policyMap)
          .map(([name, v]) => {
            const avg = v.rates.reduce((a, b) => a + b, 0) / numTasks;
            const oracleAvg = v.oracleRates.reduce((a, b) => a + b, 0) / numTasks;
            const avgJerk = v.jerks.length > 0
              ? (v.jerks.reduce((a, b) => a + b, 0) / v.jerks.length).toFixed(2)
              : '\u2014';
            const meta = v.meta;
            return {
              Policy: name,
              Affiliation: meta.Affiliation || '\u2014',
              'Success Rate': avg.toFixed(1) + '%',
              'Oracle Rate': oracleAvg.toFixed(1) + '%',
              CI: '\u2014',
              'Task Count': `${v.rates.length} / ${numTasks}`,
              Smoothness: avgJerk,
              Type: meta.Type || '\u2014',
              'Parameters (B)': meta['Parameters (B)'] || '\u2014',
              'Data Source': meta['Data Source'] || '\u2014',
              'Data Type': meta['Data Type'] || '\u2014',
              'Robot Generalist': meta['Robot Generalist'] || 'No',
              'Gen Tasks': meta['Gen Tasks'] || 'No',
              'Gen Environments': meta['Gen Environments'] || 'No',
              'Gen Embodiment': meta['Gen Embodiment'] || 'No',
              Embodiment: meta.Embodiment || '\u2014',
              'Open Sourced': meta['Open Sourced'] || 'No',
              'Data Open Sourced': meta['Data Open Sourced'] || '',
              'Needs Warmup Planning': meta['Needs Warmup Planning'] || 'No',
              'Results Logs': meta['Results Logs'] || '—',
              'Action Space': meta['Action Space'] || '\u2014',
              'Chunk Size': meta['Chunk Size'] || '\u2014',
              'Context Length': meta['Context Length'] || '\u2014',
              Paper: meta.Paper || '',
              ArXiv: meta.ArXiv || '',
              'Videos Link': meta['Videos Link'] || '',
              'Date Tested': meta['Date Tested'] || '',
              'Date Released': meta['Date Released'] || '',
            };
          });

        currentData = combined;
        taskObjectRates = {};
        sortCol = 'oracle_rate';
        sortDir = 'desc';
        renderTable();
        setTimeout(() => renderChart(), 50);
      } catch (e) {
        console.error('Failed to load combined main:', e);
      }
    }

    async function loadCombinedCustom() {
      if (customSelectedTasks.length === 0) {
        currentData = [];
        taskObjectRates = {};
        renderTable();
        return;
      }
      try {
        const selectedTasks = tasksList!.filter((t) => customSelectedTasks.includes(t.id));
        const numTasks = selectedTasks.length;
        const policyMap: Record<string, { oracleRates: number[]; endRates: number[]; jerks: number[]; meta: Record<string, string> }> = {};

        await Promise.all(
          selectedTasks.map(async (task) => {
            try {
              const metaResp = await fetch(`/benchmark/data/${task.id}/metadata.csv`);
              const metaText = await metaResp.text();
              const metaRows = parseCSV(metaText);
              const metaByFile: Record<string, Record<string, string>> = {};
              metaRows.forEach((r) => { metaByFile[r.file] = r; });

              await Promise.all(
                task.policies.map(async (file) => {
                  try {
                    const resp = await fetch(`/benchmark/data/${task.id}/${file}.csv`);
                    const text = await resp.text();
                    const lines = text.trim().split('\n').filter((l) => !l.trimStart().startsWith('#') && l.trim());
                    if (lines.length < 2) return;
                    const headers = parseCSVLine(lines[0]);
                    const rows: Record<string, string>[] = [];
                    for (let i = 1; i < lines.length; i++) {
                      const vals = parseCSVLine(lines[i]);
                      if (vals.length < headers.length) continue;
                      const row: Record<string, string> = {};
                      headers.forEach((h, idx) => { row[h.trim()] = (vals[idx] || '').trim(); });
                      rows.push(row);
                    }
                    const overall = rows.find((r) => r.category === 'OVERALL');
                    if (!overall) return;
                    const meta = metaByFile[file] || {};
                    const displayName = meta.Policy || file;
                    const oracleSr = parseFloat(overall.oracle_rate_pct || overall.success_rate_pct) || 0;
                    const hasEndRate = overall.success_rate_pct != null && overall.success_rate_pct.trim() !== '';
                    const endSr = hasEndRate ? (parseFloat(overall.success_rate_pct) || 0) : null;
                    const jerk = parseFloat(overall.jerk_joint_mean);
                    if (!policyMap[displayName]) policyMap[displayName] = { oracleRates: [], endRates: [], jerks: [], meta };
                    policyMap[displayName].oracleRates.push(oracleSr);
                    if (endSr !== null) policyMap[displayName].endRates.push(endSr);
                    if (!isNaN(jerk)) policyMap[displayName].jerks.push(jerk);
                  } catch { /* skip */ }
                })
              );
            } catch { /* skip */ }
          })
        );

        const combined = Object.entries(policyMap)
          .map(([name, v]) => {
            const oracleAvg = v.oracleRates.reduce((a, b) => a + b, 0) / numTasks;
            const hasAllEndRates = v.endRates.length === v.oracleRates.length;
            const endAvg = hasAllEndRates ? v.endRates.reduce((a, b) => a + b, 0) / numTasks : null;
            const avgJerk = v.jerks.length > 0
              ? (v.jerks.reduce((a, b) => a + b, 0) / v.jerks.length).toFixed(2)
              : '\u2014';
            const meta = v.meta;
            return {
              Policy: name,
              Affiliation: meta.Affiliation || '\u2014',
              'Oracle Rate': oracleAvg.toFixed(1) + '%',
              'Success Rate': endAvg !== null ? endAvg.toFixed(1) + '%' : '\u2014',
              CI: '\u2014',
              'Oracle CI': '\u2014',
              'Task Count': `${v.oracleRates.length} / ${numTasks}`,
              Smoothness: avgJerk,
              Type: meta.Type || '\u2014',
              'Parameters (B)': meta['Parameters (B)'] || '\u2014',
              'Data Source': meta['Data Source'] || '\u2014',
              'Data Type': meta['Data Type'] || '\u2014',
              'Robot Generalist': meta['Robot Generalist'] || 'No',
              'Gen Tasks': meta['Gen Tasks'] || 'No',
              'Gen Environments': meta['Gen Environments'] || 'No',
              'Gen Embodiment': meta['Gen Embodiment'] || 'No',
              Embodiment: meta.Embodiment || '\u2014',
              'Open Sourced': meta['Open Sourced'] || 'No',
              'Data Open Sourced': meta['Data Open Sourced'] || '',
              'Needs Warmup Planning': meta['Needs Warmup Planning'] || 'No',
              'Results Logs': meta['Results Logs'] || '—',
              'Action Space': meta['Action Space'] || '\u2014',
              'Chunk Size': meta['Chunk Size'] || '\u2014',
              'Context Length': meta['Context Length'] || '\u2014',
              Paper: meta.Paper || '',
              ArXiv: meta.ArXiv || '',
              'Videos Link': meta['Videos Link'] || '',
              'Date Tested': meta['Date Tested'] || '',
              'Date Released': meta['Date Released'] || '',
            };
          });

        currentData = combined;
        taskObjectRates = {};
        sortCol = 'oracle_rate';
        sortDir = 'desc';
        // Hide end success rate column if no policy has it, or on any combined view
        const anyHasEnd = combined.some((r) => r['Success Rate'] && r['Success Rate'] !== '\u2014');
        const isCombinedView = ['combined_easy', 'combined_main', 'combined_custom'].includes(currentTaskId);
        currentTaskFlags.oracleOnly = !anyHasEnd || isCombinedView;
        await waitForMinLoading();
        renderTable();
        setTimeout(() => renderChart(), 50);
      } catch (e) {
        console.error('Failed to load combined custom:', e);
      }
    }

    let loadingTimeout: ReturnType<typeof setTimeout> | null = null;
    function showLoading() {
      if (loadingTimeout) clearTimeout(loadingTimeout);
      loadingTimeout = setTimeout(() => {
        const tbody = root.querySelector('#leaderboard-body') as HTMLElement;
        if (tbody) {
          tbody.innerHTML = '<tr><td colspan="20" style="padding:60px 20px;text-align:center"><div style="display:inline-flex;align-items:center;gap:12px;color:var(--text-muted);font-family:\'Manrope\',sans-serif;font-size:0.9rem"><div style="width:24px;height:24px;border:3px solid var(--border);border-top-color:var(--accent);border-radius:50%;animation:lb-spin 0.8s linear infinite"></div>Loading...</div></td></tr>';
        }
      }, 300);
    }
    function hideLoading() {
      if (loadingTimeout) { clearTimeout(loadingTimeout); loadingTimeout = null; }
    }
    function waitForMinLoading(): Promise<void> {
      return Promise.resolve();
    }

    async function loadTask(taskId: string) {
      showLoading();
      try {
        const taskInfo = tasksList ? tasksList.find((t) => t.id === taskId) : null;
        currentTaskId = taskId;
        currentTaskFlags = {
          showObjects: taskInfo?.showObjects !== false,
          allowCompare: taskInfo?.allowCompare !== false,
          showOracleRate: ORACLE_RATE_TASKS.has(taskId) || taskId === 'combined_commonsense' || isCommonsenseTask(taskId),
          oracleLabel: ORACLE_LABEL_TASKS.has(taskId),
          oracleOnly: ORACLE_ONLY_TASKS.has(taskId) || isCommonsenseTask(taskId),
          showTaskCount: TASK_COUNT_TASKS.has(taskId),
        };
        const compareBtn = root.querySelector('#compare-btn') as HTMLElement;
        if (compareBtn) compareBtn.style.display = currentTaskFlags.allowCompare ? '' : 'none';
        if (!currentTaskFlags.allowCompare && compareMode) {
          compareMode = false;
          compareSelected = [];
          if (compareBtn) compareBtn.classList.remove('active');
        }

        if (taskInfo?.combined) {
          const eligibleTasks = eligibleForView(taskId);
          if (taskId === 'combined_commonsense') {
            customSelectedTasks = eligibleTasks.map((t) => t.id);
          } else if (taskId === 'combined_easy') {
            customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('ms_')).map((t) => t.id);
          } else if (taskId === 'combined_main') {
            customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('mb_')).map((t) => t.id);
          } else {
            customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('mb_') || t.id === 'ms_open' || t.id === 'ms_close').map((t) => t.id);
          }
          await loadCombinedCustom();
          return;
        }

        const policyFiles = taskInfo ? taskInfo.policies : [];
        const metaResp = await fetch(`/benchmark/data/${taskId}/metadata.csv`);
        const metaText = await metaResp.text();
        const metaRows = parseCSV(metaText);
        const metaByFile: Record<string, Record<string, string>> = {};
        metaRows.forEach((r) => {
          metaByFile[r.file] = r;
        });

        const policyData: Record<string, Record<string, string>> = {};
        const objectRates: Record<string, { name: string; rate: number }[]> = {};

        await Promise.all(
          policyFiles.map(async (file) => {
            try {
              const resp = await fetch(`/benchmark/data/${taskId}/${file}.csv`);
              const text = await resp.text();
              const lines = text
                .trim()
                .split('\n')
                .filter((l) => !l.trimStart().startsWith('#') && l.trim());
              if (lines.length < 2) return;
              const headers = parseCSVLine(lines[0]);
              const rows: Record<string, string>[] = [];
              for (let i = 1; i < lines.length; i++) {
                const vals = parseCSVLine(lines[i]);
                if (vals.length < headers.length) continue;
                const row: Record<string, string> = {};
                headers.forEach((h, idx) => {
                  row[h.trim()] = (vals[idx] || '').trim();
                });
                rows.push(row);
              }

              const overall = rows.find((r) => r.category === 'OVERALL');
              const perObj = rows.filter((r) => r.category !== 'OVERALL');

              if (overall) {
                const meta = metaByFile[file] || {};
                const rateSource = currentTaskFlags.oracleLabel ? 'oracle' : 'end';
                const sr = parseFloat(
                  rateSource === 'oracle'
                    ? overall.oracle_rate_pct || overall.success_rate_pct
                    : overall.success_rate_pct || overall.oracle_rate_pct
                ) || 0;
                const ciLo = parseFloat(
                  rateSource === 'oracle'
                    ? overall.oracle_ci_95_low_pct || overall.ci_95_low_pct
                    : overall.ci_95_low_pct || overall.oracle_ci_95_low_pct
                );
                const ciHi = parseFloat(
                  rateSource === 'oracle'
                    ? overall.oracle_ci_95_high_pct || overall.ci_95_high_pct
                    : overall.ci_95_high_pct || overall.oracle_ci_95_high_pct
                );
                const hasJerk = !isNaN(parseFloat(overall.jerk_joint_mean));
                const jerkMean = parseFloat(overall.jerk_joint_mean) || 0;
                const jerkStd = parseFloat(overall.jerk_joint_std) || 0;
                const oracleRate = overall.oracle_rate_pct ? parseFloat(overall.oracle_rate_pct).toFixed(1) + '%' : '\u2014';
                const oracleCiLo = parseFloat(overall.oracle_ci_95_low_pct);
                const oracleCiHi = parseFloat(overall.oracle_ci_95_high_pct);
                const oracleCI = !isNaN(oracleCiLo) && !isNaN(oracleCiHi)
                  ? oracleCiLo.toFixed(1) + '%\u2013' + oracleCiHi.toFixed(1) + '%'
                  : '\u2014';
                const displayName = meta.Policy || file;

                policyData[file] = {
                  _file: file,
                  Policy: displayName,
                  Affiliation: meta.Affiliation || '\u2014',
                  'Success Rate': sr.toFixed(1) + '%',
                  CI:
                    !isNaN(ciLo) && !isNaN(ciHi)
                      ? ciLo.toFixed(1) + '%\u2013' + ciHi.toFixed(1) + '%'
                      : '\u2014',
                  Smoothness: hasJerk ? jerkMean.toFixed(2) + '\u00b1' + jerkStd.toFixed(2) : '\u2014',
                  Type: meta.Type || '\u2014',
                  'Parameters (B)': meta['Parameters (B)'] || '\u2014',
                  'Oracle Rate': oracleRate,
                  'Oracle CI': oracleCI,
                  'Data Source': meta['Data Source'] || '\u2014',
              'Data Type': meta['Data Type'] || '\u2014',
                      'Robot Generalist': meta['Robot Generalist'] || 'No',
                  'Gen Tasks': meta['Gen Tasks'] || 'No',
                  'Gen Environments': meta['Gen Environments'] || 'No',
                  'Gen Embodiment': meta['Gen Embodiment'] || 'No',
                  Embodiment: meta.Embodiment || '\u2014',
                  'Open Sourced': meta['Open Sourced'] || 'No',
              'Data Open Sourced': meta['Data Open Sourced'] || '',
              'Needs Warmup Planning': meta['Needs Warmup Planning'] || 'No',
              'Results Logs': meta['Results Logs'] || '—',
                  'Action Space': meta['Action Space'] || '\u2014',
                  'Chunk Size': meta['Chunk Size'] || '\u2014',
                  'Context Length': meta['Context Length'] || '\u2014',
                  Paper: meta.Paper || '',
              ArXiv: meta.ArXiv || '',
                  'Videos Link': meta['Videos Link'] || '',
                  'Date Tested': meta['Date Tested'] || '',
                  'Date Released': meta['Date Released'] || '',
                };

                objectRates[displayName] = perObj
                  .map((r) => ({
                    name: r.category,
                    rate: parseFloat(
                      rateSource === 'oracle'
                        ? r.oracle_rate_pct || r.success_rate_pct
                        : r.success_rate_pct || r.oracle_rate_pct
                    ) || 0,
                  }))
                  .sort((a, b) => b.rate - a.rate);
              }
            } catch (err) {
              console.warn(`Failed to load ${file}.csv:`, err);
            }
          })
        );

        // Metadata-only tasks: build currentData directly from metadata rows
        if (policyFiles.length === 0) {
          currentData = metaRows
            .filter((r) => r.Policy || r.file)
            .map((r, i) => {
              const sr = parseFloat((r['Success Rate'] || r['Success Rate%'] || '0').replace('%', '')) || 0;
              const key = r.file || r.Policy || String(i);
              return {
                _file: key,
                Policy: r.Policy || key,
                Affiliation: r.Affiliation || '\u2014',
                'Success Rate': sr.toFixed(1) + '%',
                CI: '\u2014',
                Smoothness: '\u2014',
                Type: r.Type || '\u2014',
                'Parameters (B)': r['Parameters (B)'] || r.Parameters || '\u2014',
                'Data Source': r['Data Source'] || '\u2014',
                'Data Type': r['Data Type'] || '\u2014',
                'Robot Generalist': r['Robot Generalist'] || 'No',
                'Gen Tasks': r['Gen Tasks'] || 'No',
                'Gen Environments': r['Gen Environments'] || 'No',
                'Gen Embodiment': r['Gen Embodiment'] || 'No',
                Embodiment: r.Embodiment || '\u2014',
                'Open Sourced': r['Open Sourced'] || 'No',
                'Data Open Sourced': r['Data Open Sourced'] || '',
                'Needs Warmup Planning': r['Needs Warmup Planning'] || 'No',
                'Results Logs': r['Results Logs'] || '—',
                'Action Space': r['Action Space'] || '\u2014',
                'Chunk Size': r['Chunk Size'] || '\u2014',
                'Context Length': r['Context Length'] || '\u2014',
                Paper: r.Paper || '',
                ArXiv: r.ArXiv || '',
                'Videos Link': r['Videos Link'] || '',
                'Date Tested': r['Date Tested'] || '',
                'Date Released': r['Date Released'] || '',
              };
            });
        } else {
          currentData = policyFiles.filter((f) => policyData[f]).map((f) => policyData[f]);
        }
        taskObjectRates = objectRates;
        sortCol = currentData.some((r) => r['Oracle Rate'] && r['Oracle Rate'] !== '\u2014') ? 'oracle_rate' : 'success_rate';
        sortDir = 'desc';
        await waitForMinLoading();
        hideLoading();
        renderTable();
        setTimeout(() => renderChart(), 50);
      } catch (e) {
        hideLoading();
        console.error('Failed to load task:', e);
        const tbody = root.querySelector('#leaderboard-body') as HTMLElement;
        if (tbody)
          tbody.innerHTML =
            '<tr><td colspan="15" class="empty-state"><p>Failed to load data</p></td></tr>';
      }
    }

    async function initApp() {
      try {
        const resp = await fetch('/benchmark/data/index.json');
        const idx = await resp.json();
        tasksList = idx.tasks;
        const select = root.querySelector('#task') as HTMLSelectElement;
        if (select) {
          select.innerHTML = '';
          const DIVIDER_AFTER = new Set<string>();
          const SECTION_BEFORE: Record<string, string> = {
            'combined_easy': '── MolmoSpaces Manipulation ──',
            'combined_main': '── MolmoBot ──',
            'combined_commonsense': '── MolmoSpaces-Commonsense ──',
            'combined_custom': '── Combined ──',
            'ms_nav_multitype': '── MolmoSpaces Navigation ──',
          };
          tasksList!.forEach((t) => {
            if (SECTION_BEFORE[t.id]) {
              const header = document.createElement('optgroup');
              header.label = SECTION_BEFORE[t.id];
              header.disabled = true;
              select.appendChild(header);
            }
            const opt = document.createElement('option');
            opt.value = t.id;
            opt.textContent = t.label;
            select.appendChild(opt);
            if (DIVIDER_AFTER.has(t.id)) {
              const sep = document.createElement('optgroup');
              sep.label = '──────────';
              sep.disabled = true;
              select.appendChild(sep);
            }
          });
        }

        // Build custom task picker
        const picker = root.querySelector('#custom-task-picker') as HTMLElement;
        if (picker) {
          const eligibleTasks = eligibleForView('combined_custom');
          customSelectedTasks = eligibleTasks.filter((t) => t.label.includes('MB-') || t.id === 'ms_open' || t.id === 'ms_close').map((t) => t.id);
          picker.innerHTML =
            `<span class="custom-task-picker-label">Include tasks: </span>` +
            eligibleTasks.map((t) =>
              `<label class="custom-task-cb"><input type="checkbox" value="${t.id}" ${customSelectedTasks.includes(t.id) ? 'checked' : ''}> ${t.label_short ?? t.label}</label>`
            ).join('');
          picker.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
            cb.addEventListener('change', () => {
              customSelectedTasks = Array.from(
                picker.querySelectorAll('input[type="checkbox"]:checked')
              ).map((el) => (el as HTMLInputElement).value);
              if (currentTaskId === 'combined_custom') loadCombinedCustom();
            });
          });
        }
        const _urlParams = new URLSearchParams(window.location.search);


        const _viewParam = _urlParams.get('view') || (() => {
          const path = window.location.pathname;
          if (path.endsWith('/ms')) return 'ms';
          if (path.endsWith('/mb')) return 'mb';
          return '';
        })();
        const _viewMap: Record<string, string> = { ms: 'combined_easy', mb: 'combined_main', cs: 'combined_commonsense' };
        const _taskParam = _viewMap[_viewParam || ''] || _urlParams.get('task');
        const _initialTask = _taskParam && tasksList!.find((t) => t.id === _taskParam)
          ? _taskParam
          : 'combined_custom';
        if (select) select.value = _initialTask;
        if (picker) {
          const isCombined = tasksList!.find((t) => t.id === _initialTask)?.combined;
          picker.classList.toggle('visible', !!isCombined);
          updatePickerForCombined(_initialTask, picker);
          const spacer = root.querySelector('#custom-task-picker-spacer') as HTMLElement;
          if (spacer) spacer.style.display = isCombined ? '' : 'none';
        }
        if (tasksList!.length > 0) loadTask(_initialTask);
      } catch (e) {
        console.error('Failed to load task index:', e);
      }
    }

    function updatePickerForCombined(taskId: string, picker: HTMLElement) {
      if (!tasksList) return;
      const eligibleTasks = eligibleForView(taskId);

      if (taskId === 'combined_commonsense') {
        customSelectedTasks = eligibleTasks.map((t) => t.id);
      } else if (taskId === 'combined_easy') {
        customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('ms_')).map((t) => t.id);
      } else if (taskId === 'combined_main') {
        customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('mb_')).map((t) => t.id);
      } else {
        customSelectedTasks = eligibleTasks.filter((t) => t.id.startsWith('mb_') || t.id === 'ms_open' || t.id === 'ms_close').map((t) => t.id);
      }

      picker.innerHTML =
        `<span class="custom-task-picker-label">Include tasks: </span>` +
        eligibleTasks.map((t) =>
          `<label class="custom-task-cb"><input type="checkbox" value="${t.id}" ${customSelectedTasks.includes(t.id) ? 'checked' : ''}> ${t.label_short ?? t.label}</label>`
        ).join('');
      picker.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
        cb.addEventListener('change', () => {
          customSelectedTasks = Array.from(
            picker.querySelectorAll('input[type="checkbox"]:checked')
          ).map((el) => (el as HTMLInputElement).value);

          if (taskId === 'combined_commonsense') {
            currentTaskId = 'combined_commonsense';
            loadCombinedCustom();
            return;
          }

          // Determine which combined view matches the current selection
          const eligible = eligibleForView('combined_custom');
          const msIds = new Set(eligible.filter((t) => t.id.startsWith('ms_')).map((t) => t.id));
          const mbIds = new Set(eligible.filter((t) => t.id.startsWith('mb_')).map((t) => t.id));
          const defaultIds = new Set(eligible.filter((t) => t.id.startsWith('mb_') || t.id === 'ms_open' || t.id === 'ms_close').map((t) => t.id));
          const selSet = new Set(customSelectedTasks);

          const setsEqual = (a: Set<string>, b: Set<string>) => a.size === b.size && [...a].every((v) => b.has(v));

          let matchedId = 'combined_custom';
          if (setsEqual(selSet, msIds)) matchedId = 'combined_easy';
          else if (setsEqual(selSet, mbIds)) matchedId = 'combined_main';
          else if (setsEqual(selSet, defaultIds)) matchedId = 'combined_custom';
          else matchedId = 'combined_custom_user';

          const select = root.querySelector('#task') as HTMLSelectElement;
          if (select) {
            // Remove old custom option if exists
            const oldCustom = select.querySelector('option[value="combined_custom_user"]');
            if (oldCustom) oldCustom.remove();

            if (matchedId === 'combined_custom_user') {
              const opt = document.createElement('option');
              opt.value = 'combined_custom_user';
              opt.textContent = 'Custom';
              select.appendChild(opt);
            }
            select.value = matchedId === 'combined_custom_user' ? 'combined_custom_user' : matchedId;
          }
          currentTaskId = matchedId === 'combined_custom_user' ? 'combined_custom' : matchedId;

          loadCombinedCustom();
        });
      });
    }

    // ---- Event bindings ----
    const taskSelect = root.querySelector('#task') as HTMLSelectElement;
    if (taskSelect) {
      taskSelect.addEventListener('change', () => {
        const val = taskSelect.value;
        const picker = root.querySelector('#custom-task-picker') as HTMLElement;
        if (picker) {
          const isCombined = tasksList!.find((t) => t.id === val)?.combined;
          picker.classList.toggle('visible', !!isCombined);
          updatePickerForCombined(val, picker);
          const spacer = root.querySelector('#custom-task-picker-spacer') as HTMLElement;
          if (spacer) spacer.style.display = isCombined ? '' : 'none';
        }
        loadTask(val);
      });
    }

    const compareBtnEl = root.querySelector('#compare-btn');
    if (compareBtnEl) {
      compareBtnEl.addEventListener('click', () => {
        toggleCompareMode();
      });
    }

    const filterGenBtn = root.querySelector('#filter-generalist-btn');
    if (filterGenBtn) {
      filterGenBtn.addEventListener('click', () => {
        toggleGeneralistFilter();
      });
    }

    const filterNonIDBtn = root.querySelector('#filter-non-id-btn') as HTMLElement;
    if (filterNonIDBtn) {
      filterNonIDBtn.classList.add('active');
      filterNonIDBtn.addEventListener('click', () => {
        toggleNonIDFilter();
      });
    }
    // Theme toggle (follows system on first load; user toggle only lasts for this session)
    const themeToggle = root.querySelector('#theme-toggle') as HTMLButtonElement;
    const applyTheme = (mode: 'light' | 'dark') => {
      if (mode === 'light') {
        root.classList.add('light');
        if (themeToggle) themeToggle.textContent = '☀️';
      } else {
        root.classList.remove('light');
        if (themeToggle) themeToggle.textContent = '🌙';
      }
    };
    const prefersLight = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-color-scheme: light)').matches;
    applyTheme(prefersLight ? 'light' : 'dark');
    if (themeToggle) {
      themeToggle.addEventListener('click', () => {
        const isLight = root.classList.contains('light');
        applyTheme(isLight ? 'dark' : 'light');
      });
    }

    const runTestBtn = root.querySelector('#run-test-btn');
    const runTestOverlay = root.querySelector('#run-test-overlay') as HTMLElement;
    const runTestClose = root.querySelector('#run-test-close');
    if (runTestBtn && runTestOverlay) {
      runTestBtn.addEventListener('click', () => runTestOverlay.classList.add('visible'));
    }
    if (runTestClose && runTestOverlay) {
      runTestClose.addEventListener('click', () => runTestOverlay.classList.remove('visible'));
    }
    if (runTestOverlay) {
      runTestOverlay.addEventListener('click', (e) => { if (e.target === runTestOverlay) runTestOverlay.classList.remove('visible'); });
    }

    // Policy-tasks modal
    const policyTasksOverlay = root.querySelector('#policy-tasks-overlay') as HTMLElement;
    const policyTasksClose = root.querySelector('#policy-tasks-close');
    if (policyTasksClose && policyTasksOverlay) {
      policyTasksClose.addEventListener('click', () => policyTasksOverlay.classList.remove('visible'));
    }
    if (policyTasksOverlay) {
      policyTasksOverlay.addEventListener('click', (e) => { if (e.target === policyTasksOverlay) policyTasksOverlay.classList.remove('visible'); });
    }

    const _initTable = root.querySelector('#leaderboard') as HTMLElement;
    if (_initTable && filterNonID) _initTable.classList.add('hide-col-data_source');
    const _initTable2 = root.querySelector('#leaderboard-2') as HTMLElement;
    if (_initTable2 && filterNonID) _initTable2.classList.add('hide-col-data_source');

    const resetFiltersBtn = root.querySelector('#reset-filters-btn');
    if (resetFiltersBtn) {
      resetFiltersBtn.addEventListener('click', () => {
        resetFilters();
      });
    }

    const exportBtn = root.querySelector('#export-csv-btn');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => {
        exportCSV();
      });
    }

    const colPickerBtn = root.querySelector('#col-picker-btn');
    if (colPickerBtn) {
      colPickerBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleColPicker();
      });
    }

    document.addEventListener('click', () => {
      const panel = root.querySelector('#col-picker-panel') as HTMLElement;
      if (panel) panel.classList.remove('open');
      root.querySelectorAll('.col-filter-panel.open').forEach((el) => el.classList.remove('open'));
    });

    root.querySelector('#col-picker-panel')?.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    [
      'data_source', 'data_type', 'gen_tasks', 'robot_generalist',
      'data_open_sourced', 'model_open_sourced', 'type', 'embodiment', 'action_space',
    ].forEach((colKey) => {
      root.querySelector(`#col-filter-btn-${colKey}`)?.addEventListener('click', (e) => {
        e.stopPropagation();
        toggleColFilterPanel(colKey);
      });
      root.querySelector(`#col-filter-panel-${colKey}`)?.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });

    const modalOverlay = root.querySelector('#compare-modal-overlay');
    if (modalOverlay) {
      modalOverlay.addEventListener('click', (e) => {
        closeCompareModal(e);
      });
    }

    const modalClose = root.querySelector('.compare-modal-close:not(#progress-modal-close)');
    if (modalClose) {
      modalClose.addEventListener('click', () => {
        closeCompareModal(null, true);
      });
    }

    const copyBibtexBtn = root.querySelector('#copy-bibtex-btn');
    if (copyBibtexBtn) {
      copyBibtexBtn.addEventListener('click', () => {
        const pre = root.querySelector('#bibtex-content');
        if (pre) navigator.clipboard.writeText(pre.textContent || '');
      });
    }

    // Sort headers
    root.querySelectorAll('thead th').forEach((th) => {
      th.addEventListener('click', () => {
        const col = (th as HTMLElement).dataset.col;
        if (!col || col === 'rank') return;
        if (sortCol === col) {
          sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          sortCol = col;
          sortDir = (col === 'success_rate' || col === 'oracle_rate') ? 'desc' : 'asc';
        }
        renderTable();
      });
    });

    initApp();
  }, []);

  return (
    <div ref={rootRef} className="benchmark-root">
      <div className="container">
        <div className="header">
          <a href="/" style={{ display: 'inline-block' }}>
            <img
              src="https://molmospaces.allen.ai/img/MolmoSpaces.svg"
              alt="MolmoSpaces"
              className="logo-title"
              style={{ cursor: 'pointer' }}
            />
          </a>
          <p>A Large Scale Robot Policies Leaderboard</p>
        </div>

        <div className="task-selector">
          <label htmlFor="task">Task:</label>
          <select id="task" className="task-dropdown"></select>
          <a href="https://github.com/allenai/molmospaces/issues/8" target="_blank" rel="noreferrer" className="faq-link">
            Documentation
          </a>
          <a href="https://github.com/allenai/molmospaces/issues/8" target="_blank" rel="noreferrer" className="faq-link">
            Submit Results
          </a>
        </div>
        <div id="custom-task-picker" className="custom-task-picker"></div>
        <div id="custom-task-picker-spacer" style={{ display: 'none', height: 16 }}></div>

        <div
          className="controls-row"
          style={{
            marginTop: 16,
            marginBottom: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button id="filter-non-id-btn" className="filter-btn" data-tooltip="Show all policies in a single table without splitting by MolmoBot data">
                <span className="filter-radio"></span>Split by MolmoSpaces Data
              </button>
            </div>
            <button id="filter-generalist-btn" className="filter-btn" data-tooltip="Show only entries where models are trained to complete multiple tasks">
              <span className="filter-radio"></span>Hide Task Specialists
            </button>
            <button id="compare-btn" className="filter-btn">
              Compare Policies
            </button>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button id="theme-toggle" className="theme-toggle" title="Toggle light/dark mode">🌙</button>
            <div className="col-picker-wrap">
              <button id="col-picker-btn" className="filter-btn" style={{ gap: 6 }} title="Columns">
                &#9776;
              </button>
              <div className="col-picker-panel" id="col-picker-panel"></div>
            </div>
          </div>
        </div>

        {/* Compare Modal */}
        <div className="compare-modal-overlay" id="compare-modal-overlay">
          <div className="compare-modal" id="compare-modal">
            <button className="compare-modal-close">&times;</button>
            <div className="compare-cols" id="compare-cols"></div>
          </div>
        </div>

        <div id="leaderboard-gh-1" className="group-table-header" style={{display: 'none'}}>Not Using MolmoSpaces Data</div>
        <div className="table-container">
          <div className="table-scroll">
            <table id="leaderboard">
              <thead>
                <tr>
                  <th data-col="rank" rowSpan={2}>
                    #
                  </th>
                  <th data-col="policy" rowSpan={2}>
                    Policy
                  </th>
                  <th data-col="affiliation" rowSpan={2}>
                    Authors
                  </th>
                  <th
                    data-col="oracle_rate"
                    rowSpan={2}
                    data-tooltip="Oracle Success Rate: success anywhere in policy execution for a time horizon, not only at the end of the episode."
                  >
                    Oracle
                    <br />
                    Success Rate
                    <br />
                    <span style={{ fontSize: '0.72em', fontWeight: 400, opacity: 0.6 }}>%</span>
                  </th>
                  <th
                    data-col="success_rate"
                    rowSpan={2}
                    data-tooltip="Mean success percentage with reported 95% confidence interval."
                  >
                    End Success Rate
                    <br />
                    <span
                      style={{
                        fontSize: '0.72em',
                        fontWeight: 400,
                        opacity: 0.6,
                      }}
                    >
                      %
                    </span>
                  </th>
                  <th
                    data-col="task_count"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="Number of tasks with available data out of total selected tasks. Missing tasks are counted as 0% success."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Number
                    <br />
                    of Tasks
                  </th>
                  <th
                    data-col="data_source"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="In-distribution data is used for training."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    MolmoBot
                    <br />
                    Data
                    <button
                      id="col-filter-btn-data_source"
                      className="col-filter-btn"
                      title="Filter"
                    >
                      &#9776;
                    </button>
                    <div className="col-filter-panel" id="col-filter-panel-data_source"></div>
                  </th>
                  <th
                    data-col="gen_tasks"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="Indicates whether the model is trained on multiple tasks."
                    style={{ whiteSpace: 'normal' }}
                  >
                    Multi
                    <br />
                    Task
                    <button id="col-filter-btn-gen_tasks" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-gen_tasks"></div>
                  </th>
                  <th
                    data-col="robot_generalist"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="Indicates whether the model generalizes across multiple robot embodiments."
                  >
                    Robot
                    <br />
                    Gen.
                    <button id="col-filter-btn-robot_generalist" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-robot_generalist"></div>
                  </th>
                  <th
                    data-col="requires_planning"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="Indicates whether the policy needs warmup planning (e.g. TAMP)."
                  >
                    Planning
                    <br />
                    Needed
                  </th>
                  <th
                    data-col="data_type"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="The type of training data used."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Data
                    <br />
                    Type
                    <button id="col-filter-btn-data_type" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-data_type"></div>
                  </th>
                  <th
                    colSpan={2}
                    className="th-center"
                    data-col="open_sourced_group"
                    data-tooltip="Whether training data and model weights are open sourced."
                  >
                    Open Sourced
                  </th>
                  <th data-col="paper" className="th-center" rowSpan={2}>
                    Source
                  </th>
                  <th
                    data-col="type"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="Model embodiment / type."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Model
                    <br />
                    Type
                    <button id="col-filter-btn-type" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-type"></div>
                  </th>
                  <th
                    data-col="embodiment"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="The robot embodiment used for evaluation."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Embodiment
                    <button id="col-filter-btn-embodiment" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-embodiment"></div>
                  </th>
                  <th
                    data-col="action_space"
                    className="th-center"
                    rowSpan={2}
                    data-tooltip="The action space used by the policy."
                  >
                    Action
                    <br />
                    Space
                    <button id="col-filter-btn-action_space" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-action_space"></div>
                  </th>
                  <th data-col="parameters" className="th-center" rowSpan={2}>
                    Params.
                  </th>

                  <th
                    data-col="smoothness"
                    rowSpan={2}
                    data-tooltip="Mean joint jerk (rad/s&sup3;)."
                  >
                    Joint Jerk
                    <br />
                    <span
                      style={{
                        fontSize: '0.72em',
                        fontWeight: 400,
                        opacity: 0.6,
                      }}
                    >
                      rad/s&sup3;
                    </span>
                  </th>
                  <th
                    data-col="date_released"
                    rowSpan={2}
                    data-tooltip="Date the model was publicly released."
                  >
                    Date Rel.
                  </th>
                </tr>
                <tr>
                  <th
                    data-col="data_open_sourced"
                    className="th-center sub-th"
                    data-tooltip="Whether the training data is publicly available."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Data
                    <button id="col-filter-btn-data_open_sourced" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-data_open_sourced"></div>
                  </th>
                  <th
                    data-col="model_open_sourced"
                    className="th-center sub-th"
                    data-tooltip="Whether the model weights are publicly available."
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Model
                    <button id="col-filter-btn-model_open_sourced" className="col-filter-btn" title="Filter">&#9776;</button>
                    <div className="col-filter-panel" id="col-filter-panel-model_open_sourced"></div>
                  </th>
                </tr>
              </thead>
              <tbody id="leaderboard-body"></tbody>
            </table>
          </div>
        </div>
        <div id="leaderboard-gh-2" className="group-table-header" style={{display: 'none', marginTop: '6px'}}>Using MolmoSpaces Data</div>
        <div id="leaderboard-2-container" className="table-container" style={{display: 'none'}}>
          <div className="table-scroll">
            <table id="leaderboard-2">
              <thead id="leaderboard-2-thead"></thead>
              <tbody id="leaderboard-body-2"></tbody>
            </table>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <button id="export-csv-btn" className="filter-btn" style={{ gap: 6 }}>
            &#8595; Export CSV
          </button>
        </div>

        {/* Inline Progress Chart - Single */}
        <div id="progress-chart-single" className="progress-chart-outer" style={{ width: '70%', margin: '20px auto 0', display: 'none' }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)' }}>
                Benchmark Progress
              </div>
            </div>
            <div className="progress-chart-row">
              <div className="chart-wrapper progress-chart-wrapper">
                <canvas id="progressChart2"></canvas>
              </div>
              <div id="progress-legend" className="progress-legend-list"></div>
            </div>
          </div>
        </div>

        {/* Inline Progress Charts - Split (side by side) */}
        <div id="progress-chart-2" style={{ width: '90%', margin: '20px auto 0', display: 'flex', gap: 16 }}>
          <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
            <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 16 }}>
              Not Using MolmoSpaces Data
            </div>
            <div className="progress-chart-row">
              <div className="chart-wrapper progress-chart-wrapper">
                <canvas id="progressChart-nonid"></canvas>
              </div>
              <div id="progress-legend-nonid" className="progress-legend-list"></div>
            </div>
          </div>
          <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '20px 24px' }}>
            <div style={{ fontFamily: "'Manrope',sans-serif", fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: 16 }}>
              Using MolmoSpaces Data
            </div>
            <div className="progress-chart-row">
              <div className="chart-wrapper progress-chart-wrapper">
                <canvas id="progressChart-id"></canvas>
              </div>
              <div id="progress-legend-id" className="progress-legend-list"></div>
            </div>
          </div>
        </div>

        <div
          style={{
            textAlign: 'center',
            padding: '20px 24px 16px',
            color: 'var(--text-muted)',
            fontSize: '0.82rem',
            fontFamily: "'Manrope', sans-serif",
            lineHeight: 1.8,
            marginTop: 16,
          }}
        >
          <div style={{ marginBottom: 6 }}>
            Found a bug or want to add a policy?{' '}
            <a
              href="https://github.com/allenai/molmospaces/issues"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--accent)',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Open an issue on GitHub
            </a>
            {' | '}
            <a
              href="https://docs.google.com/document/d/1FcMxJgAQ_2Ojd2uu8HE2MBfD6RE53zcXa55_r8EfPts/export?format=pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--accent)',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Fair Comparisons
            </a>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 24px 20px' }}>
          <div
            style={{
              background: 'var(--surface2)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              padding: '16px 20px',
              maxWidth: 720,
              width: '100%',
              position: 'relative',
            }}
          >
            <div
              style={{
                fontFamily: "'Manrope',sans-serif",
                fontSize: '0.68rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                color: 'var(--text-muted)',
                marginBottom: 10,
              }}
            >
              Bibtex
            </div>
            <button
              id="copy-bibtex-btn"
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
                fontFamily: "'Manrope',sans-serif",
                fontSize: '0.72rem',
                fontWeight: 600,
                padding: '4px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Copy
            </button>
            <pre
              id="bibtex-content"
              style={{
                margin: 0,
                fontFamily: "'Courier New',monospace",
                fontSize: '0.78rem',
                color: 'var(--text)',
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                overflowX: 'auto',
              }}
            >
              {`@article{kim2026molmospaces,
  title={MolmoSpaces: A Large-Scale Open Ecosystem for Robot Navigation and Manipulation},
author={Kim, Yejin and Pumacay, Wilbert and Rayyan, Omar and Argus, Max and Han, Winson and VanderBilt, Eli and Salvador, Jordi and Deshpande, Abhay and Hendrix, Rose and Jauhri, Snehal and Liu, Shuo and Shafiullah, Nur Muhammad Mahi and Guru, Maya and Eftekhar, Ainaz and Farley, Karen and Clay, Donovan and Duan, Jiafei and Guru, Arjun and Wolters, Piper and Herrasti, Alvaro and Lee, Ying-Chun and Chalvatzaki, Georgia and Cui, Yuchen and Farhadi, Ali and Fox, Dieter and Krishna, Ranjay},
   journal={arXiv preprint arXiv:2602.11337},
  year={2026}
}`}
            </pre>
          </div>
        </div>

        {/* FAQ: Fair Policy Comparisons - removed, now links to external doc */}
        <div className="faq-section" id="fair-policy-comparisons" style={{display:'none'}}>
          <h2 className="faq-section-title">Fair Policy Comparisons 🍎🍎 </h2>

          <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.8, margin: '0 0 24px' }}>
            Comparing robot policies is difficult. A robotic system comprises a full stack of choices — training data, sensors, action representations, hardware — each of which can confer advantages invisible in a headline success rate. This benchmark surfaces that complexity rather than obscuring it, enabling users to construct comparisons suited to their needs.
          </p>

          <div className="faq-item" id="faq-zero-shot">
            <div className="faq-question">Generalization and Zero-Shot Transfer</div>
            <p className="faq-answer">
              The term "zero-shot" is borrowed from NLP and vision, where held-out classes are discrete and well-defined. In robotics, the boundaries are continuous and poorly specified. We therefore decompose generalization into distinct axes:
              <br /><br />
              <strong>Sim-to-Real / Real-to-Sim:</strong> MolmoBot policies are simulation-native and bridge no sim/real domain gap to run this benchmark. Policies trained primarily on real-world data must bridge a real-to-sim gap. For example, π0.5-finetune was trained for 30K steps on MolmoBot-Data to bridge this specific gap while preserving the capabilities of its underlying real-world pretraining. Policies which are simulation-native should additionally present real evaluations to validate their sim-to-real transfer.
              <br /><br />
              <strong>Environment:</strong> All policies on this benchmark are strictly environment generalists: benchmark episodes are drawn from houses held out from the training house set of MolmoSpaces.
              <br /><br />
              <strong>Object Class:</strong> Without open training data, it is difficult to verify which policies are true object-class generalists. However, the benchmark provides a proxy: Pick-Easy episodes use a limited set of common objects, while Pick episodes sample from a broad Objaverse distribution. The performance gap between these two tasks is expected to reflect object-class generalization.
              <br /><br />
              <strong>Object Instance:</strong> Generalization to novel asset instances within seen categories. We do not currently highlight this axis separately.
              <br /><br />
              <strong>Task (Verb) and Embodiment.</strong> In this form of generalization — transferring to novel manipulation primitives or novel robot morphologies. We do not yet have benchmarks testing zero-shot transfer on these axes.
            </p>
          </div>

          <div className="faq-item">
            <div className="faq-question">Training Data</div>
            <p className="faq-answer">
              We indicate whether a policy uses MolmoSpaces in-distribution (ID) data via the <strong>MolmoBot Data</strong> column. In-distribution data refers to training examples drawn from the same distribution as our benchmark tasks, which confers a measurable advantage over policies trained on entirely held-out or real-world data.
              <br /><br />
              We display ID and non-ID policies on the same leaderboard — with clear labelling and available filters — because our sim-to-real experiments show that performance improvements on MolmoSpaces benchmarks correlate with real-world performance improvements, for both ID and non-ID policies. Use the <strong>Split by MolmoSpaces Data</strong> toggle to separate ID and non-ID entries, or use the column filter to show only non-ID entries.
            </p>
          </div>

          <div className="faq-item">
            <div className="faq-question">Task Specialists vs. Generalists</div>
            <p className="faq-answer">
              We distinguish three tiers of task coverage, representing meaningfully different levels of ambition and difficulty:
              <br /><br />
              <strong>Task Specialists</strong> are trained to solve a single task — for example, picking objects and nothing else. Specialization allows a policy to exploit task-specific structure and achieve higher performance on that narrow problem, but it says little about the generality of the underlying approach. A specialist that scores 90% on picking tells you about picking, not about the policy architecture or training recipe.
              <br /><br />
              <strong>Multi-Task policies</strong> are trained on a fixed set of two or more tasks simultaneously. They must share capacity across tasks and learn representations that transfer within that set, which is a harder optimization problem than specialization. However, the task set is curated and closed — the policy is not expected to handle instructions or verbs it has not seen during training.
              <br /><br />
              <strong>Task Generalists</strong> are trained on a sufficiently broad and diverse task distribution that they can attempt a reasonably broad set of tasks. This is the hardest regime and the one most closely aligned with the long-term goal of general-purpose robot policies.
              <br /><br />
              The <strong>Hide Task Specialists</strong> toggle removes single-task policies from the leaderboard.
            </p>
          </div>

          <div className="faq-item">
            <div className="faq-question">Embodiments</div>
            <p className="faq-answer">
              This benchmark evaluates policies created on a reference embodyment, in our case often the DROID robot setup. However we adapt our benchmark to allow testing other emodyments e.g. Franka arms equipped with the CAP-gripper. These changes can result in both advatages and dissadvantages due to these changes. Strisctly speaking cross-embodiment evaluations do not allow for pure policy comparisons, they provide information as "robot systems" comparisons though. The <strong>Embodiment</strong> column and filter restrict comparisons to a single hardware platform.
            </p>
          </div>

          <div className="faq-item">
            <div className="faq-question">Robot Generalists</div>
            <p className="faq-answer">
              The <strong>Robot Generalist</strong> column indicates whether a policy is designed to operate across multiple embodiments. However, "generalist" exists on a spectrum. For example, CAP runs on multiple robot arms but requires a specific custom gripper — it generalizes across arm morphologies but not across end-effectors. Other policies may generalize across grippers but assume a fixed sensor suite. Treat this column as a starting point for investigation rather than a definitive claim.
            </p>
          </div>

          <div className="faq-item">
            <div className="faq-question">Open-Source Status</div>
            <p className="faq-answer">
              We surface whether each policy's weights and training data are publicly available, given their relevance to reproducibility and community development.
            </p>
          </div>
        </div>

        <div style={{ maxWidth: 820, margin: '16px auto 32px', padding: '0 24px', borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.7, margin: 0 }}>
            <sup>†1</sup> See <a href="https://docs.google.com/document/d/1aRJ_NGWBzdLk3jJ71GvYx-dj1nbATQbDGfSG3V4Iy0g/">docs</a> for more info on task differences.
          </p>
        </div>

        {/* About section — SEO text */}
        <div style={{ maxWidth: 820, margin: '0 auto 48px', padding: '0 24px' }}>
          <div style={{ borderTop: '2px solid var(--border-strong)', paddingTop: 32 }}>
            <h1 style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1.15rem', fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>
              About MolmoSpaces — Robotics Benchmark &amp; Leaderboard
            </h1>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.88rem', lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 14 }}>
              Real-world robot deployment demands policies that hold up across the endless variety of everyday environments — different room layouts, object shapes, surface textures, and task phrasings. Existing robotics benchmarks rarely capture this long tail of variation, making it hard to know whether a policy is genuinely robust or just well-tuned to a narrow test set.
            </p>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.88rem', lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 14 }}>
              MolmoSpaces is a fully open benchmarking ecosystem built to meet that challenge. It spans over 230,000 diverse indoor environments — from hand-crafted household scenes to procedurally generated multi-room houses — all populated with richly annotated objects and simulator-agnostic assets that work with MuJoCo, Isaac, and ManiSkill. The benchmark covers the full spectrum of embodied tasks: pick, pick-and-place, open, close, navigation, and long-horizon tasks that require coordinated perception, planning, and interaction across entire homes.
            </p>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.88rem', lineHeight: 1.8, color: 'var(--text-muted)', marginBottom: 14 }}>
              MolmoSpaces-Bench, the evaluation suite behind this leaderboard, achieves a sim-to-real correlation of R = 0.96 — meaning rankings in simulation closely predict real-robot performance. The benchmark has already confirmed that newer zero-shot VLA policies outperform earlier generations, and surfaced concrete sensitivities: prompt phrasing, initial joint positions, and camera occlusion all measurably affect success rates.
            </p>
            <p style={{ fontFamily: "'Lato', sans-serif", fontSize: '0.88rem', lineHeight: 1.8, color: 'var(--text-muted)', margin: 0 }}>
              All environments, object assets, and evaluation tooling are publicly released to support scalable data generation, policy training, and community-driven benchmark creation. If you have a vision-language-action (VLA) model or robot manipulation policy you'd like evaluated, see the submission instructions above.
            </p>
          </div>
        </div>

        {/* All-Tasks-for-Policy Modal */}
        <div id="policy-tasks-overlay" className="compare-modal-overlay">
          <div className="compare-modal" style={{ maxWidth: 720 }}>
            <button className="compare-modal-close" id="policy-tasks-close">&times;</button>
            <h3 id="policy-tasks-title" style={{ marginBottom: 4 }}>All Task Results</h3>
            <p id="policy-tasks-subtitle" style={{ marginBottom: 16, fontSize: '0.85rem', color: 'var(--text-muted)' }}></p>
            <div id="policy-tasks-body"></div>
          </div>
        </div>

        {/* Run Test Modal */}
        <div id="run-test-overlay" className="compare-modal-overlay">
          <div className="compare-modal" style={{ maxWidth: 720 }}>
            <button className="compare-modal-close" id="run-test-close">&times;</button>
            <h3 style={{ marginBottom: 4 }}>Run Evaluation Commands</h3>
            <p style={{ marginBottom: 16, fontSize: '0.85rem', color: 'var(--text-muted)' }}>MolmoSpaces Benchmarks</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Lato', monospace", fontSize: '0.82rem', lineHeight: 1.7 }}>
              {[
                { label: 'MS-Pick', cmd: `python molmo_spaces/evaluation/eval_main.py \\\n    molmo_spaces.evaluation.configs.evaluation_configs:YOUR_POLICY_CONFIG \\\n    --benchmark_dir assets/benchmarks/molmospaces-bench-v1/procthor-10k/FrankaPickDroidMiniBench/FrankaPickDroidMiniBench_json_benchmark_20251231 \\\n    --task_horizon_steps 450` },
                { label: 'MS-Pick & Place', cmd: `python molmo_spaces/evaluation/eval_main.py \\\n    molmo_spaces.evaluation.configs.evaluation_configs:YOUR_POLICY_CONFIG \\\n    --benchmark_dir assets/benchmarks/molmospaces-bench-v1/procthor-10k/FrankaPickandPlaceDroidMiniBench/FrankaPickandPlaceDroidMiniBench_20260111_json_benchmark \\\n    --task_horizon_steps 650` },
                { label: 'MS-Open', cmd: `python molmo_spaces/evaluation/eval_main.py \\\n    molmo_spaces.evaluation.configs.evaluation_configs:YOUR_POLICY_CONFIG \\\n    --benchmark_dir assets/benchmarks/molmospaces-bench-v1/ithor/FrankaOpenDataGenConfig/FrankaOpenDataGenConfig_20260123_json_benchmark \\\n    --task_horizon_steps 450` },
                { label: 'MS-Close', cmd: `python molmo_spaces/evaluation/eval_main.py \\\n    molmo_spaces.evaluation.configs.evaluation_configs:YOUR_POLICY_CONFIG \\\n    --benchmark_dir assets/benchmarks/molmospaces-bench-v1/ithor/FrankaCloseDataGenConfig/FrankaCloseDataGenConfig_20260123_json_benchmark \\\n    --task_horizon_steps 450` },
              ].map(({ label, cmd }, i) => (
                <div key={i}>
                  <strong style={{ color: 'var(--accent)' }}>{label}:</strong>
                  <div style={{ position: 'relative', margin: '6px 0 0' }}>
                    <pre style={{ background: 'var(--surface2)', padding: '10px 40px 10px 14px', borderRadius: 6, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                      {cmd}
                    </pre>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(cmd);
                        const btn = document.querySelectorAll('.copy-cmd-btn')[i] as HTMLElement;
                        if (btn) { btn.textContent = '\u2713'; setTimeout(() => { btn.textContent = '\u2398'; }, 1500); }
                      }}
                      className="copy-cmd-btn"
                      style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '1rem', padding: '2px 6px', borderRadius: 4 }}
                      title="Copy"
                    >
                      &#9112;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
